import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type {
  BenchmarkSelection,
  CompanySlug,
  CompanyView,
  DashboardResponse,
  EpochMetricId,
  JobStatusView,
  ModelView,
  RumorView,
} from "../shared/contracts.ts";
import { resolveModelScore } from "../shared/benchmark-observation.ts";
import { cadenceWindow } from "../shared/cadence-window.ts";
import "./styles.css";
import { ReleaseCandidates } from "./release-candidates.tsx";
import { LANDSCAPE_PERIOD_OPTIONS, eligibleLandscapeModels, modelReleaseDate, selectLatestScoredPricedModels, type LandscapePeriod } from "./model-selection.ts";
import { originalEvidenceLinks } from "./original-evidence.ts";
import {
  modelColorFamily,
  modelColorScheme,
  type LandscapeColoringMode,
  type ModelColorScheme,
} from "./model-coloring.ts";
import {
  DEFAULT_PRICING_ACTIVITY,
  TASK_COST_TIER_VERSION,
  effectivePricing,
  formatPrice as money,
  isTaskPricingVersion,
  priceBandFor,
  pricingActivitiesForVersion,
  pricingActivity,
  tierSchemeForVersion,
  type PriceMode,
  type PricingActivity,
  type PricingActivityId,
  type ScoreMetric,
  type TierScheme,
} from "./pricing.ts";
import { buildCapabilityChanges } from "./progress.ts";
import { EPOCH_METRICS } from "./epoch-eci.ts";
import {
  clusterLandscapeRings,
  clusterMemberships,
  individualLandscapePoints,
  LANDSCAPE_CLUSTER_MARKER_BOUND as CLUSTER_MARKER_BOUND,
  LANDSCAPE_RING_MAX_PERFORMANCE_SPAN,
  layoutCanonicalLandscapeModels,
  materializeClusterMemberships,
  placeRingChooser,
} from "./landscape-layout.ts";

type AppTab = "home" | "landscape" | "changes" | "cadence" | "signals" | "models" | "methodology";
type LandscapeMarkerMode = "rings" | "dots";

const APP_TABS: Array<{ id: AppTab; label: string }> = [
  { id: "home", label: "Start" },
  { id: "landscape", label: "Landscape" },
  { id: "models", label: "Ranks" },
  { id: "cadence", label: "Cadence" },
];

const METRICS: Array<{ key: ScoreMetric; short: string; label: string }> = [
  { key: "intelligenceIndex", short: "Intelligence", label: "AA Intelligence Index" },
  { key: "codingIndex", short: "Coding", label: "AA Coding Index" },
  { key: "agenticIndex", short: "Agentic", label: "AA Agentic Index" },
];

const CLUSTER_MARKER_RADIUS = 13.5;

function tabFromHash(hash = window.location.hash): AppTab {
  const candidate = hash.replace(/^#/, "") as AppTab;
  if (!candidate || candidate === "home") return "home";
  if (hash === "#progress") return "changes";
  if (candidate === "changes" || candidate === "signals" || candidate === "methodology") return candidate;
  return APP_TABS.some((tab) => tab.id === candidate) ? candidate : "home";
}

function formatDate(value: string | null, withYear = true): string {
  if (!value) return "Not enough data";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  }).format(new Date(value));
}

function relativeTime(value: string | null, referenceTime = Date.now()): string {
  if (!value) return "never";
  const seconds = Math.round((new Date(value).getTime() - referenceTime) / 1_000);
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [31_536_000, "year"], [2_592_000, "month"], [86_400, "day"], [3_600, "hour"], [60, "minute"],
  ];
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  for (const [size, unit] of divisions) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

function score(value: number | null): string {
  return value === null ? "—" : value.toFixed(1).replace(/\.0$/, "");
}

function benchmarkEmptyMessage(selection: BenchmarkSelection, fallback: string, aaVersionLabel: string): string {
  if (selection.source !== "epoch-eci") return `${aaVersionLabel}: ${fallback} Scores from other AA versions are not substituted.`;
  if (selection.metric === "software-engineering" || selection.metric === "math") {
    return `${EPOCH_METRICS.find((item) => item.key === selection.metric)?.label} is unavailable. Cadence will not substitute General ECI or Artificial Analysis.`;
  }
  if (selection.metric === "cyber") return "No source-qualified Cyber ECI models match this view.";
  return "No exact reviewed General ECI model projections match this view.";
}

function selectedBenchmarkEffort(model: ModelView): string | null {
  return model.benchmarkSelection?.status === "highest-effort" ? model.benchmarkSelection.selectedEffort : null;
}

function benchmarkSelectionLabel(model: ModelView): string | null {
  const effort = selectedBenchmarkEffort(model);
  if (effort) return `${effort} effort`;
  if (model.benchmarkSelection?.status === "openrouter-reported" || model.benchmarkSelection?.status === "published-configuration") return "effort unspecified";
  if (model.benchmarkSelection?.status === "variant-estimate") return "preview proxy";
  return null;
}

function compactTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 1 : 2)}k`;
  return Math.round(value).toLocaleString("en-US");
}

function tokenEconomy(model: ModelView): { label: string; className: string; measured: boolean } {
  if (model.outputTokensPerTask !== undefined && model.outputTokensPerTask !== null) {
    return { label: compactTokens(model.outputTokensPerTask), className: "token-measured", measured: true };
  }
  if (model.tokenEfficiencyScore === null) return { label: "Not measured", className: "unknown", measured: false };
  if (model.tokenEfficiencyScore >= 65) return { label: "Lean signal", className: "lean", measured: false };
  if (model.tokenEfficiencyScore <= 35) return { label: "Verbose signal", className: "verbose", measured: false };
  return { label: "Typical signal", className: "typical", measured: false };
}

function companyMap(companies: CompanyView[]): Map<CompanySlug, CompanyView> {
  return new Map(companies.map((company) => [company.slug, company]));
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia === "function" && matchMedia(query).matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return undefined;
    const media = matchMedia(query);
    const update = (): void => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

function useHorizontalOverflow<ElementType extends HTMLElement>(): [(node: ElementType | null) => void, boolean] {
  const [node, setNode] = useState<ElementType | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    if (!node || typeof ResizeObserver !== "function") { setOverflowing(false); return undefined; }
    const update = (): void => setOverflowing(node.scrollWidth > node.clientWidth + 1);
    update();
    const frame = requestAnimationFrame(update);
    const observer = new ResizeObserver(update);
    const observeChildren = (): void => { for (const child of node.children) observer.observe(child); };
    observer.observe(node);
    observeChildren();
    const mutations = typeof MutationObserver === "function" ? new MutationObserver(() => { observeChildren(); update(); }) : null;
    mutations?.observe(node, { childList: true });
    return () => { cancelAnimationFrame(frame); mutations?.disconnect(); observer.disconnect(); };
  }, [node]);
  return [setNode, overflowing];
}

function navigateHorizontalOverflow(event: React.KeyboardEvent<HTMLDivElement>): void {
  const direction = event.key === "Home" ? "start"
    : event.key === "End" ? "end"
      : event.key === "ArrowLeft" ? "left"
        : event.key === "ArrowRight" ? "right"
          : null;
  if (direction === null) return;
  event.preventDefault();
  const viewport = event.currentTarget;
  const left = direction === "start" ? 0
    : direction === "end" ? viewport.scrollWidth
      : viewport.scrollLeft + (direction === "left" ? -1 : 1) * viewport.clientWidth * 0.7;
  viewport.scrollTo({ left, behavior: "auto" });
}

function StatusPill({ jobs }: { jobs: JobStatusView[] }): React.ReactElement {
  const hourly = jobs.find((job) => job.jobType === "openrouter");
  const failed = jobs.some((job) => job.status === "failed");
  const running = jobs.some((job) => job.status === "running");
  return (
    <div className={`status-pill ${failed ? "is-failed" : running ? "is-running" : ""}`} title={failed ? "A collection job needs attention" : "Collectors operational"}>
      <span className="status-dot" />
      <span>{failed ? "Collector issue" : running ? "Scanning" : "Live catalog"}</span>
      {hourly?.finishedAt && <time>{relativeTime(hourly.finishedAt)}</time>}
    </div>
  );
}

function CompanyFilters({
  companies,
  active,
  onChange,
}: {
  companies: CompanyView[];
  active: CompanySlug | "all";
  onChange: (company: CompanySlug | "all") => void;
}): React.ReactElement {
  const values: ReadonlyArray<CompanySlug | "all"> = ["all", ...companies.map((company) => company.slug)];
  return (
    <div className="company-filters" role="radiogroup" aria-label="Filter by company">
      <button role="radio" tabIndex={active === "all" ? 0 : -1} aria-checked={active === "all"} className={active === "all" ? "active" : ""} onKeyDown={(event) => radioKey(event, values, active, onChange)} onClick={() => onChange("all")}>All</button>
      {companies.map((company) => (
        <button
          key={company.slug}
          role="radio"
          tabIndex={active === company.slug ? 0 : -1}
          aria-checked={active === company.slug}
          className={active === company.slug ? "active" : ""}
          onKeyDown={(event) => radioKey(event, values, active, onChange)}
          onClick={() => onChange(company.slug)}
          style={{ "--company": company.color } as React.CSSProperties}
        >
          <span className="filter-dot" aria-hidden="true" />{company.shortName}
        </button>
      ))}
    </div>
  );
}

function ColoringFilters({ active, onChange }: { active: LandscapeColoringMode; onChange: (mode: LandscapeColoringMode) => void }): React.ReactElement {
  const values: readonly LandscapeColoringMode[] = ["uniform", "name"];
  return (
    <div className="coloring-filters" role="radiogroup" aria-label="Color models">
      <button role="radio" tabIndex={active === "uniform" ? 0 : -1} aria-checked={active === "uniform"} className={active === "uniform" ? "active" : ""} onKeyDown={(event) => radioKey(event, values, active, onChange)} onClick={() => onChange("uniform")}>Uniform</button>
      <button role="radio" tabIndex={active === "name" ? 0 : -1} aria-checked={active === "name"} className={active === "name" ? "active" : ""} onKeyDown={(event) => radioKey(event, values, active, onChange)} onClick={() => onChange("name")}>By name</button>
    </div>
  );
}

function MarkerModeFilters({ active, onChange }: { active: LandscapeMarkerMode; onChange: (mode: LandscapeMarkerMode) => void }): React.ReactElement {
  const values: readonly LandscapeMarkerMode[] = ["dots", "rings"];
  return (
    <div className="marker-mode-filters" role="radiogroup" aria-label="Landscape marker mode">
      <button role="radio" tabIndex={active === "dots" ? 0 : -1} aria-checked={active === "dots"} className={active === "dots" ? "active" : ""} onKeyDown={(event) => radioKey(event, values, active, onChange)} onClick={() => onChange("dots")}>Dots</button>
      <button role="radio" tabIndex={active === "rings" ? 0 : -1} aria-checked={active === "rings"} className={active === "rings" ? "active" : ""} onKeyDown={(event) => radioKey(event, values, active, onChange)} onClick={() => onChange("rings")}>Rings</button>
    </div>
  );
}

function LandscapeCompanySelect({ companies, active, onChange, labelledBy }: {
  companies: CompanyView[];
  active: CompanySlug | "all";
  onChange: (company: CompanySlug | "all") => void;
  labelledBy: string;
}): React.ReactElement {
  return <select className="landscape-company-select" aria-labelledby={labelledBy} value={active} onChange={(event) => onChange(event.target.value as CompanySlug | "all")}>
    <option value="all">All labs</option>
    {companies.map((company) => <option key={company.slug} value={company.slug}>{company.name}</option>)}
  </select>;
}

function PeriodFilters({ active, onChange }: { active: LandscapePeriod; onChange: (period: LandscapePeriod) => void }): React.ReactElement {
  return (
    <div className="period-filters" role="radiogroup" aria-label="Filter landscape scope">
      {LANDSCAPE_PERIOD_OPTIONS.map((option) => (
        <button
          key={option.value}
          role="radio"
          tabIndex={active === option.value ? 0 : -1}
          aria-checked={active === option.value}
          className={active === option.value ? "active" : ""}
          title={option.title}
          onKeyDown={(event) => radioKey(event, LANDSCAPE_PERIOD_OPTIONS.map((item) => item.value), active, onChange)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ActivityFilters({ activities, active, onChange }: { activities: ReadonlyArray<PricingActivity>; active: PricingActivityId; onChange: (activity: PricingActivityId) => void }): React.ReactElement {
  return (
    <div className="activity-filters" role="radiogroup" aria-label="Price activity">
      {activities.map((activity) => (
        <button
          key={activity.id}
          role="radio"
          tabIndex={active === activity.id ? 0 : -1}
          aria-checked={active === activity.id}
          className={active === activity.id ? "active" : ""}
          onKeyDown={(event) => radioKey(event, activities.map((item) => item.id), active, onChange)}
          onClick={() => onChange(activity.id)}
        >
          <strong>{activity.id === "agentic-coding" ? "Agentic" : activity.id === "research" ? "Research" : activity.label}</strong>
          <small>{activity.sessionLabel}</small>
        </button>
      ))}
    </div>
  );
}

function ActivityExplainer({ activity }: { activity: PricingActivity }): React.ReactElement {
  return <p className="activity-explainer"><span>{activity.sessionLabel}</span> {activity.description}</p>;
}

function LandscapeChart({
  models,
  companies,
  metric,
  selection,
  aaVersionLabel,
  priceMode,
  activity,
  costTierVersion,
  tierScheme,
  coloringMode,
  colorScheme,
  markerMode,
  selectedId,
  isVisible,
  compact,
  archiveWithoutProjections,
  onSelect,
}: {
  models: ModelView[];
  companies: CompanyView[];
  metric: ScoreMetric;
  selection: BenchmarkSelection;
  aaVersionLabel: string;
  priceMode: PriceMode;
  activity: PricingActivity;
  costTierVersion: string;
  tierScheme: TierScheme;
  coloringMode: LandscapeColoringMode;
  colorScheme: ModelColorScheme | null;
  markerMode: LandscapeMarkerMode;
  selectedId: string | null;
  isVisible: boolean;
  compact: boolean;
  archiveWithoutProjections: boolean;
  onSelect: (id: string) => void;
}): React.ReactElement {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [chartWidth, setChartWidth] = useState(960);
  const chartHeight = compact ? 356 : 422;
  const bandBottom = chartHeight - (compact ? 24 : 40);
  const scoreBaseline = bandBottom - 22;
  const scoreSpan = scoreBaseline - 45;
  const tierLabelY = chartHeight - 25;
  const tierRangeY = chartHeight - 7;
  const labelBottom = bandBottom;
  const colors = useMemo(() => new Map(companies.map((company) => [company.slug, company.color] as const)), [companies]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const chooserRef = useRef<HTMLElement>(null);
  const triggerRefs = useRef(new Map<string, SVGGElement>());
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());
  const clusterTouch = useRef<{ x: number; y: number } | null>(null);
  const optionTouch = useRef<{ key: string; x: number; y: number } | null>(null);
  const chooserHadFocus = useRef(false);
  const [openClusterKey, setOpenClusterKey] = useState<string | null>(null);
  const [selectionStatus, setSelectionStatus] = useState("");

  const layout = useMemo(() => layoutCanonicalLandscapeModels({
    models,
    metric,
    selection,
    priceMode,
    activity,
    costTierVersion,
    tierScheme,
    coloringMode,
    colorScheme,
    companyColors: colors,
    chartWidth,
    scoreBaseline,
    scoreSpan,
  }), [models, metric, selection, priceMode, activity, costTierVersion, tierScheme, coloringMode, colorScheme, colors, chartWidth, scoreBaseline, scoreSpan]);
  const layouts = layout.points;
  const announcementFor = (model: ModelView, value: number): string => {
    const observation = resolveModelScore(model, selection);
    const metricLabel = selection.source === "epoch-eci" ? `Epoch ECI ${EPOCH_METRICS.find((item) => item.key === selection.metric)?.label}` : METRICS.find((item) => item.key === selection.metric)?.label;
    const interval = observation?.interval.status === "published"
      ? `Published 90% interval ${score(observation.interval.low)} to ${score(observation.interval.high)}.`
      : observation?.interval.status === "unavailable" ? `Interval unavailable: ${observation.interval.reason}` : "Interval unavailable.";
    return `${model.shortName}, ${metricLabel}, ${selection.source === "epoch-eci" ? "published ECI central estimate" : observation?.estimated ? "estimated score" : "measured score"} ${score(value)}. ${interval}`;
  };
  const maxScore = layout.maxScore;
  const scoreDomain = layout.scoreDomain;
  const scoreTicks = layout.scoreTicks;
  const tierSlot = layout.tierSlot;
  const finalClusters = useMemo(
    () => markerMode === "rings"
      ? clusterLandscapeRings(layouts, {
        performanceValue: (point) => point.value,
        maxPerformanceSpan: LANDSCAPE_RING_MAX_PERFORMANCE_SPAN,
      })
      : individualLandscapePoints(layouts),
    [layouts, markerMode],
  );
  const finalMemberships = useMemo(() => clusterMemberships(finalClusters), [finalClusters]);
  const finalClusterSignature = finalMemberships.map((cluster) => cluster.key).join("\u001e");
  const [animatedMemberships, setAnimatedMemberships] = useState(() => finalMemberships);
  const clusters = useMemo(
    () => reducedMotion ? finalClusters : materializeClusterMemberships(animatedMemberships, layouts),
    [reducedMotion, finalClusters, animatedMemberships, layouts],
  );
  const overlappingClusters = useMemo(
    () => markerMode === "rings" ? clusters.filter((cluster) => cluster.members.length > 1) : [],
    [clusters, markerMode],
  );
  const clusterByMember = useMemo(() => new Map(clusters.flatMap((cluster) => cluster.members.map((member) => [member.id, cluster] as const))), [clusters]);
  const openCluster = markerMode === "rings" && openClusterKey
    ? overlappingClusters.find((cluster) => cluster.key === openClusterKey) ?? null
    : null;
  const clusterSignature = clusters.map((cluster) => cluster.key).join("\u001e");
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const update = (): void => {
      if (viewport.clientWidth > 0) {
        const availableWidth = Math.round(viewport.clientWidth);
        setChartWidth((current) => current === availableWidth ? current : availableWidth);
      }
    };
    update();
    const frame = requestAnimationFrame(update);
    const timer = window.setTimeout(update, 50);
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    return () => { cancelAnimationFrame(frame); window.clearTimeout(timer); observer.disconnect(); };
  }, [compact, isVisible]);
  const ringDisplayMembers = new Map(overlappingClusters.map((cluster) => {
    const selectedMember = cluster.members.find((member) => member.id === selectedId);
    const defaultMember = [...cluster.members].sort((left, right) => right.value - left.value || left.id.localeCompare(right.id))[0]!;
    return [cluster.key, selectedMember ?? defaultMember] as const;
  }));
  const ringPosition = (cluster: (typeof overlappingClusters)[number]): { x: number; y: number } => {
    const member = ringDisplayMembers.get(cluster.key);
    return member ? { x: member.x, y: member.y } : { x: cluster.x, y: cluster.y };
  };

  useEffect(() => {
    if (reducedMotion) {
      setAnimatedMemberships(finalMemberships);
      return undefined;
    }
    const timer = window.setTimeout(() => setAnimatedMemberships(finalMemberships), 1_050);
    return () => window.clearTimeout(timer);
  }, [finalClusterSignature, reducedMotion]);

  useEffect(() => {
    if (!openClusterKey || overlappingClusters.some((cluster) => cluster.key === openClusterKey)) return;
    const shouldRestoreFocus = chooserHadFocus.current;
    chooserHadFocus.current = false;
    setOpenClusterKey(null);
    if (!shouldRestoreFocus) return;
    requestAnimationFrame(() => {
      const replacement = selectedId ? clusterByMember.get(selectedId) : undefined;
      if (replacement?.members.length && triggerRefs.current.get(replacement.key)) triggerRefs.current.get(replacement.key)?.focus();
      else viewportRef.current?.focus();
    });
  }, [clusterSignature, openClusterKey, overlappingClusters, clusterByMember, selectedId]);

  const legendFamilies = colorScheme?.families.filter((family) => layouts.some((point) => point.family?.id === family.id)) ?? [];
  const legendGroups = colorScheme?.groups?.map((group) => ({
    ...group,
    families: group.familyIds.map((id) => legendFamilies.find((family) => family.id === id)).filter((family) => family !== undefined),
  })).filter((group) => group.families.length > 0) ?? [];
  const estimatePatternColors = [...new Set(layouts.filter((point) => point.estimated).map((point) => point.color))];
  const estimatePatternId = (color: string): string => `estimate-stripes-${color.replace(/[^a-z0-9]/gi, "")}`;

  const requestedLabelIds: string[] = !compact && selectedId ? [selectedId] : [];
  for (const company of compact ? [] : companies) {
    const companyPoints = layouts.filter((point) => point.model.companySlug === company.slug);
    const highest = [...companyPoints].sort((left, right) => right.value - left.value || left.id.localeCompare(right.id))[0];
    const latest = [...companyPoints].sort((left, right) => right.model.catalogedAt.localeCompare(left.model.catalogedAt) || left.id.localeCompare(right.id))[0];
    if (latest && !requestedLabelIds.includes(latest.id)) requestedLabelIds.push(latest.id);
    if (highest && !requestedLabelIds.includes(highest.id)) requestedLabelIds.push(highest.id);
  }

  interface LabelBox { left: number; top: number; right: number; bottom: number }
  interface LabelLayout { id: string; text: string; x: number; y: number; anchor: "start" | "end"; color: string; selected: boolean; box: LabelBox }
  interface ClusterObstacle extends LabelBox { clusterKey: string }
  const clusterObstacles: ClusterObstacle[] = overlappingClusters.map((cluster) => {
    const position = ringPosition(cluster);
    return {
      clusterKey: cluster.key,
      left: position.x - CLUSTER_MARKER_BOUND,
      top: position.y - CLUSTER_MARKER_BOUND,
      right: position.x + CLUSTER_MARKER_BOUND,
      bottom: position.y + CLUSTER_MARKER_BOUND,
    };
  });
  const visualMarkers = markerMode === "rings"
    ? clusters.map((cluster) => cluster.members.length > 1
      ? { key: cluster.key, ...ringPosition(cluster) }
      : { key: cluster.key, x: cluster.x, y: cluster.y })
    : layouts.map((point) => ({ key: point.id, x: point.x, y: point.y }));
  const acceptedBoxes: LabelBox[] = [];
  const labels: LabelLayout[] = [];
  const placedLabelMarkers = new Set<string>();
  const boxesOverlap = (left: LabelBox, right: LabelBox): boolean =>
    left.left < right.right + 4 && left.right > right.left - 4 && left.top < right.bottom + 3 && left.bottom > right.top - 3;

  for (const id of requestedLabelIds) {
    const requestedPoint = layouts.find((candidate) => candidate.id === id);
    if (!requestedPoint) continue;
    const pointCluster = clusterByMember.get(id);
    const grouped = markerMode === "rings" && Boolean(pointCluster?.members.length && pointCluster.members.length > 1);
    const point = grouped ? ringDisplayMembers.get(pointCluster!.key) ?? requestedPoint : requestedPoint;
    if (compact && point.id === selectedId) continue;
    const text = point.model.shortName.length > 22 ? `${point.model.shortName.slice(0, 20)}…` : point.model.shortName;
    const width = Math.max(30, text.length * 6.2);
    const markerKey = grouped ? pointCluster!.key : id;
    if (placedLabelMarkers.has(markerKey)) continue;
    placedLabelMarkers.add(markerKey);
    const position = grouped ? ringPosition(pointCluster!) : point;
    const pointX = position.x;
    const pointY = position.y;
    const ownObstacles = grouped
      ? clusterObstacles.filter((obstacle) => obstacle.clusterKey === pointCluster!.key)
      : [];
    const rightX = Math.max(pointX + 11, ...ownObstacles.map((obstacle) => obstacle.right + 8));
    const leftX = Math.min(pointX - 11, ...ownObstacles.map((obstacle) => obstacle.left - 8));
    const options = [
      { x: rightX, y: pointY - 10, anchor: "start" as const, box: { left: rightX - 3, top: pointY - 22, right: rightX - 3 + width, bottom: pointY - 6 } },
      { x: rightX, y: pointY + 19, anchor: "start" as const, box: { left: rightX - 3, top: pointY + 6, right: rightX - 3 + width, bottom: pointY + 22 } },
      { x: leftX, y: pointY - 10, anchor: "end" as const, box: { left: leftX + 3 - width, top: pointY - 22, right: leftX + 3, bottom: pointY - 6 } },
      { x: leftX, y: pointY + 19, anchor: "end" as const, box: { left: leftX + 3 - width, top: pointY + 6, right: leftX + 3, bottom: pointY + 22 } },
    ];
    const anchorHash = [...point.id].reduce((hash, character) => ((hash * 31) + character.charCodeAt(0)) >>> 0, 0);
    const stableAnchor: "start" | "end" = anchorHash % 2 === 0 ? "start" : "end";
    const selected = point.id === selectedId;
    const eligibleOptions = selected && ownObstacles.length > 0
      ? options
      : options.filter((option) => option.anchor === stableAnchor);
    const scoredOptions = eligibleOptions.map((option) => {
      const pointCollisions = visualMarkers.filter((other) => other.key !== markerKey
        && other.x >= option.box.left - 13 && other.x <= option.box.right + 13
        && other.y >= option.box.top - 13 && other.y <= option.box.bottom + 13).length;
      const labelCollisions = acceptedBoxes.filter((box) => boxesOverlap(option.box, box)).length;
      const clusterCollisions = clusterObstacles.filter((obstacle) => boxesOverlap(option.box, obstacle)).length;
      const outside = option.box.left < 8 || option.box.right > chartWidth - 8 || option.box.top < 38 || option.box.bottom > labelBottom;
      return { ...option, collisions: pointCollisions + labelCollisions + clusterCollisions + (outside ? 100 : 0) };
    }).sort((left, right) => left.collisions - right.collisions);
    const placement = scoredOptions[0];
    if (!placement || (!selected && placement.collisions > 0)) continue;
    acceptedBoxes.push(placement.box);
    labels.push({ id: point.id, text, x: placement.x, y: placement.y, anchor: placement.anchor, color: point.color, selected, box: placement.box });
  }

  const orderedLayouts = [...layouts].sort((left, right) => Number(left.id === selectedId) - Number(right.id === selectedId) || left.id.localeCompare(right.id));
  const clusterDomId = (key: string): string => `overlap-cluster-${overlappingClusters.findIndex((cluster) => cluster.key === key)}`;
  const listNames = (names: string[]): string => names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  const segmentPath = (index: number, count: number): string => {
    const gap = Math.min(5, 18 / count);
    const startDegrees = -90 + index * (360 / count) + gap / 2;
    const endDegrees = -90 + (index + 1) * (360 / count) - gap / 2;
    const pointAt = (degrees: number): [number, number] => {
      const radians = degrees * Math.PI / 180;
      return [Math.cos(radians) * CLUSTER_MARKER_RADIUS, Math.sin(radians) * CLUSTER_MARKER_RADIUS];
    };
    const [startX, startY] = pointAt(startDegrees);
    const [endX, endY] = pointAt(endDegrees);
    return `M ${startX} ${startY} A ${CLUSTER_MARKER_RADIUS} ${CLUSTER_MARKER_RADIUS} 0 ${endDegrees - startDegrees > 180 ? 1 : 0} 1 ${endX} ${endY}`;
  };
  const focusChooser = (cluster: (typeof overlappingClusters)[number]): void => {
    const ordered = [...cluster.members].sort((left, right) => right.value - left.value || left.id.localeCompare(right.id));
    const target = ordered.find((member) => member.id === selectedId) ?? ordered[0];
    if (target) optionRefs.current.get(`${cluster.key}:${target.id}`)?.focus();
  };
  const toggleCluster = (cluster: (typeof overlappingClusters)[number]): void => {
    if (openClusterKey === cluster.key) {
      setOpenClusterKey(null);
      return;
    }
    chooserHadFocus.current = false;
    setOpenClusterKey(cluster.key);
    requestAnimationFrame(() => focusChooser(cluster));
  };
  const activateChartAt = (chart: SVGSVGElement, clientX: number, clientY: number): boolean => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return false;

    interface ActivationCandidate {
      cluster: (typeof overlappingClusters)[number] | undefined;
      modelId?: string;
      distance: number;
      visualPriority: number;
      order: number;
    }
    const candidates: ActivationCandidate[] = [];
    const clusterForNode = (node: Element): (typeof overlappingClusters)[number] | undefined => {
      const key = node.closest<SVGGElement>(".cluster-control")?.dataset.clusterKey;
      return key ? overlappingClusters.find((cluster) => cluster.key === key) : undefined;
    };

    // Painted ring strokes and point centers are treated as
    // semantic activation sites. Choosing the nearest site corrects whichever
    // transparent SVG target happened to win DOM hit testing across partitions.
    chart.querySelectorAll<SVGPathElement>(".cluster-marker path").forEach((path, order) => {
      const matrix = path.getScreenCTM();
      if (!matrix) return;
      const localPoint = new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse());
      if (!path.isPointInStroke(localPoint)) return;
      const cluster = clusterForNode(path);
      if (cluster) candidates.push({ cluster, distance: 0, visualPriority: 2, order });
    });
    chart.querySelectorAll<SVGCircleElement>(".cluster-affordance-hit").forEach((surface, order) => {
      const bounds = surface.getBoundingClientRect();
      if (!(bounds.width > 0) || !(bounds.height > 0)) return;
      const x = (bounds.left + bounds.right) / 2;
      const y = (bounds.top + bounds.bottom) / 2;
      const normalizedDistance = Math.hypot((clientX - x) / (bounds.width / 2), (clientY - y) / (bounds.height / 2));
      if (normalizedDistance > 1) return;
      const cluster = clusterForNode(surface);
      if (cluster) candidates.push({ cluster, distance: Math.hypot(clientX - x, clientY - y), visualPriority: 2, order });
    });
    chart.querySelectorAll<SVGCircleElement>(".model-point .point-core").forEach((core, order) => {
      const modelId = core.closest<SVGGElement>(".model-point")?.dataset.modelId;
      const cluster = modelId ? clusterByMember.get(modelId) : undefined;
      if (!modelId || !cluster) return;
      const bounds = core.getBoundingClientRect();
      const coreRadius = Number(core.getAttribute("r"));
      if (!(coreRadius > 0) || !(bounds.width > 0) || !(bounds.height > 0)) return;
      const x = (bounds.left + bounds.right) / 2;
      const y = (bounds.top + bounds.bottom) / 2;
      const targetRadius = cluster.members.length > 1 ? 12 : 20;
      const radiusX = targetRadius * bounds.width / (coreRadius * 2);
      const radiusY = targetRadius * bounds.height / (coreRadius * 2);
      const normalizedDistance = Math.hypot((clientX - x) / radiusX, (clientY - y) / radiusY);
      if (normalizedDistance > 1) return;
      const centerDistance = Math.hypot(clientX - x, clientY - y);
      candidates.push({ cluster, modelId, distance: centerDistance, visualPriority: 1, order });
    });

    const activation = candidates.sort((left, right) =>
      left.distance - right.distance || right.visualPriority - left.visualPriority || right.order - left.order)[0];
    if (!activation) return false;
    if (activation.cluster?.members.length && activation.cluster.members.length > 1) toggleCluster(activation.cluster);
    else if (activation.modelId) {
      onSelect(activation.modelId);
      const point = layouts.find((candidate) => candidate.model.id === activation.modelId);
      if (point) setSelectionStatus(announcementFor(point.model, point.value));
    }
    return true;
  };
  const handleChartPointerActivation = (event: React.MouseEvent<SVGSVGElement>): void => {
    if (event.target instanceof Element && event.target.closest(".ring-chooser-foreign")) return;
    const { clientX, clientY } = event.nativeEvent;
    // Keyboard/programmatic activation has no pointer geometry and should keep
    // using the focused control's normal click/keydown handler.
    if (clientX === 0 && clientY === 0) return;
    if (!activateChartAt(event.currentTarget, clientX, clientY)) return;
    event.preventDefault();
    event.stopPropagation();
  };
  const handleChartTouchStart = (event: React.TouchEvent<SVGSVGElement>): void => {
    const target = event.target;
    if (event.touches.length !== 1 || !(target instanceof Element) || !target.closest(".cluster-control")) {
      clusterTouch.current = null;
      return;
    }
    const touch = event.touches[0]!;
    clusterTouch.current = { x: touch.clientX, y: touch.clientY };
  };
  const handleChartTouchMove = (event: React.TouchEvent<SVGSVGElement>): void => {
    const started = clusterTouch.current;
    const touch = event.touches[0];
    if (!started || !touch) return;
    if (Math.hypot(touch.clientX - started.x, touch.clientY - started.y) > 10) clusterTouch.current = null;
  };
  const handleChartTouchEnd = (event: React.TouchEvent<SVGSVGElement>): void => {
    const started = clusterTouch.current;
    clusterTouch.current = null;
    const touch = event.changedTouches[0];
    if (!started || !touch || Math.hypot(touch.clientX - started.x, touch.clientY - started.y) > 10) return;
    if (!activateChartAt(event.currentTarget, touch.clientX, touch.clientY)) return;
    event.preventDefault();
    event.stopPropagation();
  };
  const closeChooser = (): void => {
    if (!openCluster) return;
    chooserHadFocus.current = false;
    setOpenClusterKey(null);
    requestAnimationFrame(() => triggerRefs.current.get(openCluster.key)?.focus());
  };
  const chooserMembers = openCluster
    ? [...openCluster.members].sort((left, right) => right.value - left.value || left.id.localeCompare(right.id))
    : [];
  const openClusterPosition = openCluster ? ringPosition(openCluster) : null;
  const chooserWidth = Math.min(compact ? 260 : 230, chartWidth - 16);
  const chooserHeight = openCluster
    ? Math.min(Math.max(142, 54 + chooserMembers.length * 52), compact ? 320 : 280)
    : 0;
  const chartTop = viewportRef.current?.getBoundingClientRect().top ?? 0;
  const chooserPlacement = openClusterPosition ? placeRingChooser({
    ring: openClusterPosition, width: chooserWidth, height: chooserHeight, chartWidth,
    visibleTop: 72 - chartTop, visibleBottom: window.innerHeight - chartTop - 16,
  }) : null;

  const legends = legendFamilies.length > 0 && (
    <div className="chart-legends">
      {legendFamilies.length > 0 && (
        <div className={`family-color-legend ${legendGroups.length > 0 ? "grouped" : ""}`} aria-label={`${colorScheme?.companySlug ?? "Model"} name color legend`}>
          <b>Family</b>
          {legendGroups.length > 0 ? (
            <div className="family-legend-groups">
              {legendGroups.map((group) => (
                <div className="family-legend-group" key={group.id}>
                  <em>{group.label}</em>
                  <span className="family-legend-items">
                    {group.families.map((family) => <span className="family-legend-item" key={family.id}><i style={{ background: family.color }} />{family.label}</span>)}
                  </span>
                </div>
              ))}
            </div>
          ) : legendFamilies.map((family) => <span className="family-legend-item" key={family.id}><i style={{ background: family.color }} />{family.label}</span>)}
        </div>
      )}
    </div>
  );

  return (
    <div className={`chart-wrap ${openCluster ? "chooser-open" : ""} ${!layouts.length ? "chart-empty-wrap" : ""}`}>
      {!layouts.length && <div className="chart-empty" role="status">{selection.source === "epoch-eci" && archiveWithoutProjections ? "Model-level ECI projections were not captured in this archive." : benchmarkEmptyMessage(selection, "No eligible scored and priced models match these filters.", aaVersionLabel)}</div>}
      <div className="chart-plot">
        <div className="chart-y-label">higher {selection.source === "epoch-eci" ? EPOCH_METRICS.find((item) => item.key === selection.metric)?.short.toLowerCase() : METRICS.find((item) => item.key === metric)?.short.toLowerCase()}</div>
        <div
          className="chart-viewport"
        ref={viewportRef}
        tabIndex={-1}
      >
        <svg
          className={`landscape-chart ${compact ? "compact" : ""}`}
          style={{ width: chartWidth, height: chartHeight }}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          role="group"
          aria-label={`${selection.source === "epoch-eci" ? `Epoch ECI ${EPOCH_METRICS.find((item) => item.key === selection.metric)?.label}` : METRICS.find((item) => item.key === metric)?.label} by cost tier, ${markerMode} mode`}
          onClickCapture={handleChartPointerActivation}
          onTouchStart={handleChartTouchStart}
          onTouchMove={handleChartTouchMove}
          onTouchCancel={() => { clusterTouch.current = null; }}
          onTouchEnd={handleChartTouchEnd}
        >
          <defs>
            <filter id="point-glow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            {estimatePatternColors.map((color) => (
              <pattern key={color} id={estimatePatternId(color)} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" fill={color} />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#050608" strokeWidth="3" />
              </pattern>
            ))}
          </defs>
          {tierScheme.order.map((tier, index) => (
            <g key={tier}>
              <rect x={index * tierSlot + 6} y="35" width={tierSlot - 2} height={bandBottom - 35} className={`tier-band tier-${tier}`} />
              {!compact && <text x={index * tierSlot + 6 + (tierSlot - 2) / 2} y={tierLabelY} textAnchor="middle" className="axis-tier">{tierScheme.tiers[tier].name}</text>}
              <text x={index * tierSlot + 6 + (tierSlot - 2) / 2} y={tierRangeY} textAnchor="middle" className="axis-tier-range">{compact ? `${money(tierScheme.tiers[tier].min)}+` : tierScheme.tiers[tier].range}</text>
            </g>
          ))}
          {scoreTicks.map((tick) => {
            const y = scoreBaseline - ((tick - scoreDomain[0]) / (scoreDomain[1] - scoreDomain[0])) * scoreSpan;
            return <g key={tick}><line x1="6" x2={chartWidth - 6} y1={y} y2={y} className="chart-grid" /><text x="12" y={y - 7} className="axis-score">{tick}</text></g>;
          })}
          {orderedLayouts.map((point) => {
            const { model, value, estimated, x, y, color, family, pricing, band } = point;
            const selected = model.id === selectedId;
            const cluster = clusterByMember.get(model.id)!;
            const grouped = markerMode === "rings" && cluster.members.length > 1;
            if (grouped) return null;
            const accessibleFamily = family ?? modelColorFamily(model);
            const pointCopy = `${model.shortName}, ${accessibleFamily ? `${accessibleFamily.label} family, ` : ""}${selection.source === "epoch-eci" ? "published ECI central estimate" : estimated ? "estimated score" : "measured score"} ${score(value)}, ${tierScheme.tiers[band].name} tier`;
            return (
              <g
                key={model.id}
                className={`model-point singleton ${selected ? "selected" : ""}`}
                data-model-id={model.id}
                data-price={pricing.value ?? undefined}
                data-tier={band}
                data-color-family={family?.id}
                role="button"
                tabIndex={0}
                aria-label={pointCopy}
                aria-pressed={selected}
                onClick={() => { onSelect(model.id); setSelectionStatus(announcementFor(model, value)); }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  onSelect(model.id);
                  setSelectionStatus(announcementFor(model, value));
                }}
              >
                <circle className="point-focus" cx={x} cy={y} r="17" />
                <circle className="point-hit" cx={x} cy={y} r="22" fill="transparent" />
                <circle className="point-halo" cx={x} cy={y} r={selected ? 15 : 11} fill={color} opacity={estimated ? (selected ? 0.2 : 0.09) : (selected ? 0.25 : 0.12)} filter={selected ? "url(#point-glow)" : undefined} />
                <circle className={`point-core ${estimated ? "estimated" : ""}`} cx={x} cy={y} r={estimated ? (selected ? 8 : 6) : (selected ? 7 : 5)} fill={estimated ? `url(#${estimatePatternId(color)})` : color} />
                <title>{model.shortName}: {family ? `${family.label} · ` : ""}{selection.source === "epoch-eci" ? "published ECI central estimate " : estimated ? "estimated " : "measured "}{score(value)}{selection.source === "artificial-analysis" && benchmarkSelectionLabel(model) ? ` · ${benchmarkSelectionLabel(model)}` : ""} · {money(pricing.value)}{pricing.isTaskPrice ? ` ${activity.label} task` : "/M blended"} · {pricing.source}</title>
              </g>
            );
          })}
          <g className="chart-labels" aria-hidden="true">
            {labels.map((label) => (
              <text key={label.id} x="0" y="0" transform={`translate(${label.x} ${label.y})`} textAnchor={label.anchor} data-model-id={label.id} className={`point-label ${label.selected ? "selected" : ""}`} style={{ fill: label.color }}>{label.text}</text>
            ))}
          </g>
          <g className="landscape-clusters">
            {overlappingClusters.map((cluster) => {
              const selectedMember = cluster.members.find((member) => member.id === selectedId);
              const displayMember = ringDisplayMembers.get(cluster.key)!;
              const position = ringPosition(cluster);
              const values = cluster.members.map((member) => member.value);
              const bands = [...new Set(cluster.members.map((member) => tierScheme.tiers[member.band].name))];
              const chooserId = clusterDomId(cluster.key);
              const selected = Boolean(selectedMember);
              const accessibleName = `${cluster.members.length} overlapping models: ${listNames(cluster.members.map((member) => member.model.shortName))}. ${bands.join(" and ")} ${bands.length === 1 ? "tier" : "tiers"}, scores ${score(Math.min(...values))} to ${score(Math.max(...values))}. Activate to choose.`;
              return (
                <g
                  key={cluster.key}
                  className={`cluster-control ring ${selected ? "selected" : ""} ${openClusterKey === cluster.key ? "open" : ""}`}
                  data-cluster-key={cluster.key}
                  data-cluster-count={cluster.members.length}
                  data-performance-span={Math.max(...values) - Math.min(...values)}
                  data-member-ids={cluster.members.map((member) => member.id).join("\u001f")}
                  data-selected-model-id={displayMember.id}
                  data-center-x={position.x}
                  data-center-y={position.y}
                  role="button"
                  tabIndex={0}
                  aria-label={accessibleName}
                  aria-expanded={openClusterKey === cluster.key}
                  aria-controls={openClusterKey === cluster.key ? chooserId : undefined}
                  ref={(node) => { if (node) triggerRefs.current.set(cluster.key, node); else triggerRefs.current.delete(cluster.key); }}
                  onClick={() => toggleCluster(cluster)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    toggleCluster(cluster);
                  }}
                >
                  <circle className="cluster-affordance-hit" cx={position.x} cy={position.y} r="22" />
                  <circle className="cluster-focus" cx={position.x} cy={position.y} r="18" />
                  <circle className="point-halo cluster-center-halo" cx={position.x} cy={position.y} r="11" fill={displayMember.color} opacity={displayMember.estimated ? 0.09 : 0.12} />
                  <circle className={`point-core cluster-center-core ${displayMember.estimated ? "estimated" : ""}`} cx={position.x} cy={position.y} r="5" fill={displayMember.estimated ? `url(#${estimatePatternId(displayMember.color)})` : displayMember.color} />
                  <g className="cluster-marker" transform={`translate(${position.x} ${position.y})`}>
                    {cluster.members.map((member, index) => <path key={member.id} d={segmentPath(index, cluster.members.length)} stroke={member.color} strokeWidth={member.id === displayMember.id ? 3 : 2} />)}
                  </g>
                </g>
              );
            })}
          </g>
          {openCluster && chooserPlacement && (
            <g className="ring-chooser-layer">
              <line
                className="ring-chooser-leader"
                x1={chooserPlacement.ringX}
                y1={chooserPlacement.ringY}
                x2={chooserPlacement.edgeX}
                y2={chooserPlacement.edgeY}
              />
              <foreignObject
                className="ring-chooser-foreign"
                data-placement={chooserPlacement.side}
                x={chooserPlacement.x}
                y={chooserPlacement.y}
                width={chooserPlacement.width}
                height={chooserPlacement.height}
              >
                <section
                  className="ring-chooser"
                  id={clusterDomId(openCluster.key)}
                  ref={chooserRef}
                  aria-labelledby={`${clusterDomId(openCluster.key)}-heading`}
                  onFocusCapture={() => { chooserHadFocus.current = true; }}
                  onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) chooserHadFocus.current = false;
                  }}
                  onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); closeChooser(); } }}
                >
                  <div className="ring-chooser-heading">
                    <div><p>Overlapping models</p><h3 id={`${clusterDomId(openCluster.key)}-heading`}>Choose model <span>{chooserMembers.length}</span></h3></div>
                    <button className="ring-chooser-close" type="button" onClick={closeChooser} aria-label="Close overlapping model chooser">×</button>
                  </div>
                  <div className="ring-chooser-options">
                    {chooserMembers.map((member, index) => {
                      const family = modelColorFamily(member.model);
                      const selected = member.id === selectedId;
                      return (
                        <button
                          type="button"
                          key={member.id}
                          className="ring-chooser-option"
                          data-model-id={member.id}
                          data-point-x={member.x}
                          data-point-y={member.y}
                          aria-pressed={selected}
                          ref={(node) => { const key = `${openCluster.key}:${member.id}`; if (node) optionRefs.current.set(key, node); else optionRefs.current.delete(key); }}
                          onClick={() => {
                            onSelect(member.id);
                            setSelectionStatus(`${announcementFor(member.model, member.value)} Selected from ${openCluster.members.length} overlapping models.`);
                            closeChooser();
                          }}
                          onTouchStart={(event) => {
                            if (event.touches.length !== 1) { optionTouch.current = null; return; }
                            const touch = event.touches[0]!;
                            optionTouch.current = { key: member.id, x: touch.clientX, y: touch.clientY };
                          }}
                          onTouchMove={(event) => {
                            const started = optionTouch.current;
                            const touch = event.touches[0];
                            if (!started || started.key !== member.id || !touch) return;
                            if (Math.hypot(touch.clientX - started.x, touch.clientY - started.y) > 10) optionTouch.current = null;
                          }}
                          onTouchCancel={() => { optionTouch.current = null; }}
                          onTouchEnd={(event) => {
                            const started = optionTouch.current;
                            optionTouch.current = null;
                            const touch = event.changedTouches[0];
                            if (!started || started.key !== member.id || !touch || Math.hypot(touch.clientX - started.x, touch.clientY - started.y) > 10) return;
                            event.preventDefault();
                            event.currentTarget.click();
                          }}
                          onKeyDown={(event) => {
                            if (event.key === " ") { event.preventDefault(); event.currentTarget.click(); return; }
                            const targetIndex = event.key === "Home" ? 0
                              : event.key === "End" ? chooserMembers.length - 1
                                : event.key === "ArrowRight" || event.key === "ArrowDown" ? (index + 1) % chooserMembers.length
                                  : event.key === "ArrowLeft" || event.key === "ArrowUp" ? (index - 1 + chooserMembers.length) % chooserMembers.length
                                    : null;
                            if (targetIndex === null) return;
                            event.preventDefault();
                            optionRefs.current.get(`${openCluster.key}:${chooserMembers[targetIndex]!.id}`)?.focus();
                          }}
                        >
                          <span className="ring-option-identity"><i style={{ background: member.color }} /><span><small>{member.model.companyName}{family ? ` · ${family.label}` : ""}</small><strong>{member.model.shortName}</strong><em>{selection.source === "epoch-eci" ? "Published ECI central estimate" : member.estimated ? "Estimated" : "Measured"} · {member.pricing.source}{member.pricing.fallback ? " · fallback" : ""}{member.pricing.cacheFallback ? " · cache→input" : ""}</em></span></span>
                          <span className="ring-option-values"><strong>{member.estimated ? "≈" : ""}{score(member.value)}</strong><small>{money(member.pricing.value)} · {tierScheme.tiers[member.band].name}</small></span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              </foreignObject>
            </g>
          )}
          </svg>
        </div>
      </div>
      {legends}
      <p className="sr-only" aria-live="polite">{selectionStatus}</p>
    </div>
  );
}

function ModelInspector({ model, company, aaVersionLabel, priceMode, activity, costTierVersion }: { model: ModelView; company: CompanyView | undefined; aaVersionLabel: string; priceMode: PriceMode; activity: PricingActivity; costTierVersion: string }): React.ReactElement {
  const pricing = effectivePricing(model, priceMode, activity, costTierVersion);
  const benchmarkLabel = benchmarkSelectionLabel(model);
  const benchmarkSuffix = benchmarkLabel ? ` · ${benchmarkLabel}` : "";
  const launchEvidenceLinks = originalEvidenceLinks(model);
  const intelligenceSourceText = model.benchmarkSelection?.status === "openrouter-reported"
    ? `OpenRouter · ${aaVersionLabel}`
    : model.benchmarkSelection?.status === "variant-estimate"
      ? `${aaVersionLabel} variant estimate`
      : `${aaVersionLabel} ${model.intelligenceIndexEstimated ? "estimate" : "measured score"}`;
  return (
    <article className="model-inspector" style={{ "--company": company?.color ?? "#fff" } as React.CSSProperties}>
      <div className="inspector-identity">
        <span className="eyebrow"><span className="company-mark" /> {company?.shortName}</span>
        <h3>{model.shortName}</h3>
      </div>
      <dl className="inspector-metrics">
        <div className="benchmark-stat intelligence"><dt>Intelligence {model.intelligenceIndexEstimated && <span className="estimate-badge">estimate</span>}</dt><dd>{model.intelligenceIndexEstimated ? "≈" : ""}{score(model.intelligenceIndex)}</dd><small>{model.intelligenceIndex === null ? `No retained ${aaVersionLabel} score` : model.intelligenceSourceUrl ? <a href={model.intelligenceSourceUrl} target="_blank" rel="noreferrer">{intelligenceSourceText}{benchmarkSuffix} ↗</a> : `${aaVersionLabel} Index`}</small></div>
        <div><dt>{pricing.isTaskPrice ? "Task price" : "Blended price"}</dt><dd>{money(pricing.value)}</dd><small>{pricing.source}{pricing.fallback ? " · fallback" : ""}{pricing.cacheFallback ? " · cache billed as input" : ""}{priceMode === "original" ? launchEvidenceLinks.map((source) => <React.Fragment key={source.url}> · <a href={source.url} target="_blank" rel="noreferrer">{source.label} ↗</a></React.Fragment>) : priceMode === "msrp" && model.msrpProvenance?.supplement ? <> · <a href={model.msrpProvenance.supplement.sourceUrl} target="_blank" rel="noreferrer">supplement ↗</a></> : priceMode === "msrp" && model.msrpProvenance?.routeStatus === "official-route" ? <> · <a href={model.msrpProvenance.endpointUrl} target="_blank" rel="noreferrer">official route ↗</a></> : null}</small></div>
        <div className="token-rates"><dt>Token prices <span>USD / 1M</span></dt><dd>{([["Cache", pricing.cacheRead], ["Input", pricing.input], ["Output", pricing.output]] as const).map(([label, value]) => <span key={label}><small>{label}</small><strong>{money(value)}</strong></span>)}</dd></div>
        <div className="release-date"><dt>Release date</dt><dd>{formatDate(modelReleaseDate(model, priceMode)?.displayValue ?? null)}</dd><small>{priceMode === "original" ? "Original launch" : model.dateKind === "official" ? "Official release" : "OpenRouter catalog date"}</small></div>
      </dl>
    </article>
  );
}

function CadenceRows({ companies, active, onSelect, referenceDate }: { companies: CompanyView[]; active: CompanySlug | "all"; onSelect: (slug: CompanySlug) => void; referenceDate: string }): React.ReactElement {
  const now = new Date(referenceDate).getTime();
  const { start: timelineStart, end: timelineEnd } = cadenceWindow(new Date(referenceDate));
  const timelineSpan = timelineEnd - timelineStart;
  const positionFor = (date: string): number => ((new Date(date).getTime() - timelineStart) / timelineSpan) * 100;
  return (
    <div className="cadence-list">
      {companies.map((company) => {
        const daysUntil = company.projectedNextAt ? Math.ceil((new Date(company.projectedNextAt).getTime() - now) / 86_400_000) : null;
        const visibleReleases = company.releaseHistory.filter((release) => {
          const time = new Date(release.date).getTime();
          return time >= timelineStart && time <= timelineEnd;
        });
        return (
          <button
            className={`cadence-row ${active === company.slug ? "active" : ""}`}
            key={company.slug}
            type="button"
            aria-pressed={active === company.slug}
            onClick={() => onSelect(company.slug)}
            style={{ "--company": company.color } as React.CSSProperties}
          >
            <span className="cadence-company"><span className="company-mark" /> <strong>{company.shortName}</strong><small>{company.modelFamilies.join(" · ")}</small></span>
            <span className="cadence-number">
              <strong>{company.medianCadenceDays === null ? "—" : `${company.medianCadenceDays}d`}</strong>
              <small>{company.cadenceRangeDays ? `${company.cadenceRangeDays[0]}–${company.cadenceRangeDays[1]}d middle range` : "need more releases"}</small>
            </span>
            <span className="cadence-timeline">
              <span className="timeline-line" aria-hidden="true" />
              <span className="now-line" aria-label="Now" style={{ left: `${positionFor(referenceDate)}%` }}><i>Now</i></span>
              {visibleReleases.map((release) => {
                const label = `${release.modelNames.join(" / ")} · ${formatDate(release.date)}`;
                return <span key={release.date} className="release-tick" style={{ left: `${positionFor(release.date)}%` }} title={label} data-tooltip={label} role="img" aria-label={label} />;
              })}
              {company.projectedReleaseDates.filter((date) => Date.parse(date) >= timelineStart && Date.parse(date) <= timelineEnd).map((date) => {
                const overdue = new Date(date).getTime() < now;
                const label = `${overdue ? "Missed" : "Expected"} cadence beat · ${formatDate(date)}`;
                return <span key={date} className={`projected-tick ${overdue ? "overdue" : "future"}`} style={{ left: `${positionFor(date)}%` }} title={label} data-tooltip={label} role="img" aria-label={label} />;
              })}
            </span>
            <span className="cadence-next">
              <strong>{daysUntil === null ? "Unknown" : daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? "Expected now" : `~${daysUntil}d`}</strong>
              <small>{company.projectedNextAt ? `first unfilled beat · ${company.cadenceConfidence} confidence` : "next cadence window"}</small>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RumorCard({ rumor, company }: { rumor: RumorView; company: CompanyView | undefined }): React.ReactElement {
  const kindLabel: Record<RumorView["kind"], string> = {
    official_teaser: "Official signal",
    roadmap: "Roadmap",
    credible_reporting: "Reported",
    rumor: "Rumor",
  };
  const expected = rumor.expectedStart
    ? rumor.expectedEnd && rumor.expectedEnd !== rumor.expectedStart
      ? `${formatDate(rumor.expectedStart)} – ${formatDate(rumor.expectedEnd)}`
      : formatDate(rumor.expectedStart)
    : "No credible date yet";
  return (
    <article className="signal-card" style={{ "--company": company?.color ?? "#fff" } as React.CSSProperties}>
      <div className="signal-topline">
        <span className={`signal-kind kind-${rumor.kind}`}>{kindLabel[rumor.kind]}</span>
        <span className="confidence"><i style={{ width: `${Math.round(rumor.confidence * 100)}%` }} />{Math.round(rumor.confidence * 100)}% confidence</span>
      </div>
      <span className="eyebrow"><span className="company-mark" />{company?.shortName} · {rumor.modelName}</span>
      <h3>{rumor.headline}</h3>
      <p>{rumor.summary}</p>
      <div className="signal-footer">
        <span><small>Expected</small>{expected}</span>
        <span className="signal-sources">
          {rumor.sources.slice(0, 3).map((source, index) => (
            <a href={source.url} target="_blank" rel="noreferrer" key={source.url} title={source.title} aria-label={`Source ${index + 1}: ${source.title} (opens in a new tab)`}>{index + 1}</a>
          ))}
          <small>{rumor.sources.length} source{rumor.sources.length === 1 ? "" : "s"}</small>
        </span>
      </div>
    </article>
  );
}

function ChangesPanel({
  models, companies, selection, aaVersionLabel, referenceDate,
}: {
  models: ModelView[];
  companies: CompanyView[];
  selection: BenchmarkSelection;
  aaVersionLabel: string;
  referenceDate: string;
}): React.ReactElement {
  const changes = useMemo(() => buildCapabilityChanges(models, { referenceDate, selection }), [models, referenceDate, selection]);
  const [company, setCompany] = useState<CompanySlug | "all">("all");
  const [days, setDays] = useState(90);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const end = Date.parse(referenceDate);
  const start = end - days * 86_400_000;
  const inWindow = changes.filter((change) => Date.parse(change.date) >= start);
  const visible = inWindow.filter((change) => company === "all" || change.companySlug === company);
  const keyFor = (change: (typeof changes)[number]): string => `${change.companySlug}:${change.date}`;
  const selected = visible.find((change) => keyFor(change) === selectedKey) ?? visible.at(-1);
  // Carry the last pre-window record forward; never restart a lab's best at zero.
  const series = companies.map((lab) => {
    const history = changes.filter((change) => change.companySlug === lab.slug);
    const baseline = history.filter((change) => Date.parse(change.date) < start).at(-1);
    return { lab, points: [...(baseline ? [{ ...baseline, date: new Date(start).toISOString() }] : []), ...history.filter((change) => Date.parse(change.date) >= start)] };
  });
  const values = series.flatMap((line) => line.points.map((point) => point.score));
  const low = values.length ? Math.floor((Math.min(...values) - 2) / 5) * 5 : 0;
  const high = values.length ? Math.ceil((Math.max(...values) + 2) / 5) * 5 : 10;
  const compact = useMediaQuery("(max-width: 840px)");
  const width = compact ? 360 : 960, height = compact ? 320 : 400;
  const xFor = (date: string): number => 38 + (Date.parse(date) - start) / (end - start) * (width - 56);
  const yFor = (value: number): number => 24 + (high - value) / (high - low) * (height - 62);
  return <div className="changes-panel">
    <div className="changes-controls">
      <label>Lab<select aria-label="Changes lab" value={company} onChange={(event) => setCompany(event.target.value as CompanySlug | "all")}><option value="all">All labs</option>{companies.map((lab) => <option key={lab.slug} value={lab.slug}>{lab.name}</option>)}</select></label>
      <label>Window<select aria-label="Changes window" value={days} onChange={(event) => setDays(Number(event.target.value))}><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option><option value={730}>2 years</option></select></label>
    </div>
    {series.some((line) => line.points.length && (company === "all" || line.lab.slug === company)) ? <>
      <svg className="changes-chart" viewBox={`0 0 ${width} ${height}`} role="group" aria-label="Best measured capability by lab over release dates">
        {Array.from({ length: 5 }, (_, i) => low + (high - low) * i / 4).map((tick) => <g key={tick}><line x1={38} x2={width - 18} y1={yFor(tick)} y2={yFor(tick)} className="chart-grid" /><text x={30} y={yFor(tick) + 4} textAnchor="end">{score(tick)}</text></g>)}
        {[0, 0.5, 1].map((fraction) => { const date = new Date(start + fraction * (end - start)).toISOString(); return <text key={fraction} x={xFor(date)} y={height - 12} textAnchor={fraction === 0 ? "start" : fraction === 1 ? "end" : "middle"}>{formatDate(date, false)}</text>; })}
        {series.filter((line) => line.points.length && (company === "all" || line.lab.slug === company)).map(({ lab, points }) => <g key={lab.slug} style={{ color: lab.color }}>
          <path className="changes-line" data-company={lab.slug} d={points.map((point, index) => `${index === 0 ? "M" : "H"}${xFor(point.date)}${index === 0 ? "," : " V"}${yFor(point.score)}`).join(" ") + ` H${width - 18}`} />
          {points.filter((point) => inWindow.some((change) => keyFor(change) === keyFor(point))).map((change) => <g key={keyFor(change)} className={`change-point ${selected && keyFor(selected) === keyFor(change) ? "selected" : ""}`} role="button" tabIndex={0} aria-pressed={selected && keyFor(selected) === keyFor(change)} aria-label={`${lab.name}: ${change.modelNames.join(", ")}, ${score(change.score)}, ${formatDate(change.date)}`} onClick={() => setSelectedKey(keyFor(change))} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedKey(keyFor(change)); } }}>
            <circle className="change-hit" cx={xFor(change.date)} cy={yFor(change.score)} r={15} /><circle cx={xFor(change.date)} cy={yFor(change.score)} r={4} fill="currentColor" />
          </g>)}
        </g>)}
      </svg>
      <div className="changes-legend">{series.filter((line) => line.points.length && (company === "all" || line.lab.slug === company)).map(({ lab }) => <span key={lab.slug}><i style={{ background: lab.color }} />{lab.shortName}</span>)}</div>
      {selected ? <div className="change-detail" aria-live="polite">
        <label>Inspect a release<select aria-label="Capability change" value={keyFor(selected)} onChange={(event) => setSelectedKey(event.target.value)}>{[...visible].reverse().map((change) => <option key={keyFor(change)} value={keyFor(change)}>{formatDate(change.date, false)} · {change.modelNames.join(" / ")}</option>)}</select></label>
        <div><span>{companies.find((lab) => lab.slug === selected.companySlug)?.name} · {formatDate(selected.date)}</span><h3>{selected.modelNames.join(" / ")}</h3><strong>{selected.previousScore === null ? score(selected.score) : `${score(selected.previousScore)} → ${score(selected.score)}`}</strong><p>{selected.previousScore === null ? "First retained measured release for this lab." : `+${score(selected.score - selected.previousScore)} over this lab’s previous best.`}</p></div>
      </div> : <p className="changes-empty" role="status">No new capability records in this window. Lines carry forward the earlier best.</p>}
    </> : <p className="changes-empty" role="status">{benchmarkEmptyMessage(selection, "No measured releases match this view.", aaVersionLabel)}</p>}
    <p className="source-note">Each step is a new per-lab best among retained, measured models—not an average or a price comparison. {selection.source === "artificial-analysis" ? `${aaVersionLabel} only; estimates excluded.` : "Published ECI central estimates only; uncertainty is not a claim of a statistically significant improvement."} Official dates where recorded; otherwise catalog dates. Missing or retired scored models may leave gaps in this reconstruction.</p>
  </div>;
}

function ComparisonTable({ models, companies, metric, selection, aaVersionLabel, priceMode, activity, costTierVersion, tierScheme }: { models: ModelView[]; companies: CompanyView[]; metric: ScoreMetric; selection: BenchmarkSelection; aaVersionLabel: string; priceMode: PriceMode; activity: PricingActivity; costTierVersion: string; tierScheme: TierScheme }): React.ReactElement {
  const colors = companyMap(companies);
  const tableHintId = useId().replace(/:/g, "");
  const [setTableViewport, tableOverflow] = useHorizontalOverflow<HTMLDivElement>();
  const benchmarkName = selection.source === "epoch-eci" ? `Epoch ECI ${EPOCH_METRICS.find((item) => item.key === selection.metric)?.short}` : `${aaVersionLabel} ${METRICS.find((item) => item.key === metric)?.short}`;
  const priceColumns = isTaskPricingVersion(costTierVersion)
    ? [
      { label: "Story task", activity: pricingActivity("story-without-tools", costTierVersion) },
      { label: "Agentic task", activity: pricingActivity("agentic-coding", costTierVersion) },
    ]
    : [{ label: "Blended", activity }];
  const ranked = [...models]
    .filter((model) => resolveModelScore(model, selection) !== null)
    .sort((a, b) => (resolveModelScore(b, selection)?.value ?? -Infinity) - (resolveModelScore(a, selection)?.value ?? -Infinity))
    .slice(0, 40);
  return (
    <>
      {!ranked.length && <div className="progress-empty comparison-empty" role="status">{benchmarkEmptyMessage(selection, "No scored models match this ranking view.", aaVersionLabel)}</div>}
      {tableOverflow && <p className="chart-navigation-hint table-navigation-hint" id={tableHintId}>Scroll horizontally to reach every ranking column.</p>}
      <div className="table-shell" ref={setTableViewport} tabIndex={tableOverflow ? 0 : -1} aria-label={tableOverflow ? "Scrollable model ranking table" : undefined} aria-describedby={tableOverflow ? tableHintId : undefined}>
      <table>
        <caption className="sr-only">{benchmarkName} model ranking</caption>
        <thead><tr><th>#</th><th>Model</th><th>{selection.source === "epoch-eci" ? "ECI" : METRICS.find((item) => item.key === metric)?.short}</th><th>Cost band</th>{priceColumns.map((column) => <th key={column.label}>{column.label}</th>)}<th>Output / task</th><th>Date</th></tr></thead>
        <tbody>
          {ranked.map((model, index) => {
            const color = colors.get(model.companySlug)?.color;
            const economy = tokenEconomy(model);
            const pricing = effectivePricing(model, priceMode, activity, costTierVersion);
            const priceBand = priceBandFor(pricing, tierScheme);
            const observation = resolveModelScore(model, selection)!;
            const benchmarkLabel = selection.source === "epoch-eci" ? EPOCH_METRICS.find((item) => item.key === selection.metric)?.short : benchmarkSelectionLabel(model);
            const scoreKind = [observation.estimated ? "estimate" : null, benchmarkLabel].filter(Boolean).join(" · ");
            return (
              <tr key={model.id}>
                <td className="rank">{String(index + 1).padStart(2, "0")}</td>
                <td><span className="table-model"><i style={{ background: color }} /><strong>{model.shortName}</strong><small>{model.companyName}</small></span></td>
                <td><strong className={`table-score ${observation.estimated ? "estimated" : ""}`}>{observation.estimated ? "≈" : ""}{score(observation.value)}</strong>{scoreKind && <small className="score-kind">{scoreKind}</small>}</td>
                <td><span className={`tier-chip tier-${priceBand}`}>{tierScheme.tiers[priceBand].symbol} · {tierScheme.tiers[priceBand].name}</span></td>
                {priceColumns.map((column) => {
                  const task = effectivePricing(model, priceMode, column.activity, costTierVersion);
                  return <td key={column.label} title={task.source}><span className="rank-task-price">{money(task.value)}</span>{!task.isTaskPrice && <small className="unit"> / M</small>}{(task.fallback || task.cacheFallback) && <small className="price-source">{[task.fallback && "Fallback route", task.cacheFallback && "Cache billed as input"].filter(Boolean).join(" · ")}</small>}{priceMode === "original" && originalEvidenceLinks(model).map((source) => <a className="price-source" key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label} ↗</a>)}</td>;
                })}
                <td><span className={`economy ${economy.className}`}>{economy.label}</span></td>
                <td><time>{formatDate(modelReleaseDate(model, priceMode)?.displayValue ?? null, false)}</time><small className="date-kind">{priceMode === "original" ? "launch" : model.dateKind}</small></td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
      <ol className="mobile-ranking" aria-label={`${benchmarkName} ranking`}>
        {ranked.map((model, index) => {
          const color = colors.get(model.companySlug)?.color;
          const economy = tokenEconomy(model);
          const observation = resolveModelScore(model, selection)!;
          const estimated = observation.estimated;
          const pricing = effectivePricing(model, priceMode, activity, costTierVersion);
          const priceBand = priceBandFor(pricing, tierScheme);
          const benchmarkLabel = selection.source === "epoch-eci" ? `published ECI central estimate · ${EPOCH_METRICS.find((item) => item.key === selection.metric)?.short}` : benchmarkSelectionLabel(model);
          const scoreKind = [estimated ? "estimate" : selection.source === "artificial-analysis" ? METRICS.find((item) => item.key === selection.metric)?.short : null, benchmarkLabel].filter(Boolean).join(" · ");
          return (
            <li className={`mobile-rank-card ${estimated ? "estimated" : ""}`} key={model.id} style={{ "--company": color } as React.CSSProperties}>
              <div className="mobile-rank-head">
                <span className="rank">{String(index + 1).padStart(2, "0")}</span>
                <span className="table-model"><i style={{ background: color }} /><strong>{model.shortName}</strong><small>{model.companyName}</small></span>
                <strong className={`table-score ${estimated ? "estimated" : ""}`}>{estimated ? "≈" : ""}{score(observation.value)}<small>{scoreKind}</small></strong>
              </div>
              <dl className="mobile-rank-details">
                {priceColumns.map((column) => {
                  const task = effectivePricing(model, priceMode, column.activity, costTierVersion);
                  return <div key={column.label}><dt>{column.label}</dt><dd title={task.source}><span className="rank-task-price">{money(task.value)}</span>{!task.isTaskPrice && <small className="unit"> / M</small>}{(task.fallback || task.cacheFallback) && <small className="price-source">{[task.fallback && "Fallback route", task.cacheFallback && "Cache billed as input"].filter(Boolean).join(" · ")}</small>}{priceMode === "original" && originalEvidenceLinks(model).map((source) => <a className="price-source" key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label} ↗</a>)}</dd></div>;
                })}
                <div><dt>Cost band</dt><dd><span className={`tier-chip tier-${priceBand}`}>{tierScheme.tiers[priceBand].name}</span></dd></div>
                <div><dt>Output / task</dt><dd><span className={`economy ${economy.className}`}>{economy.label}</span></dd></div>
                <div><dt>Date</dt><dd><time>{formatDate(modelReleaseDate(model, priceMode)?.displayValue ?? null, false)}</time></dd></div>
              </dl>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function radioKey<T extends string>(event: React.KeyboardEvent<HTMLButtonElement>, values: readonly T[], active: T, change: (value: T) => void): void {
  const index = values.indexOf(active);
  const next = event.key === "Home" ? 0 : event.key === "End" ? values.length - 1
    : event.key === "ArrowRight" || event.key === "ArrowDown" ? (index + 1) % values.length
      : event.key === "ArrowLeft" || event.key === "ArrowUp" ? (index - 1 + values.length) % values.length : null;
  if (next === null) return;
  const group = event.currentTarget.parentElement;
  event.preventDefault(); change(values[next]!);
  requestAnimationFrame(() => group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus());
}

function EmptyState(): React.ReactElement {
  return (
    <main className="empty-page">
      <div className="empty-orbit"><span /><span /><span /></div>
      <p className="eyebrow">Collectors warming up</p>
      <h1>The landscape is<br />coming into focus.</h1>
      <p>Cadence is making its first catalog pass. Refresh in a moment.</p>
    </main>
  );
}

function App(): React.ReactElement {
  const requestedArchiveAt = new URLSearchParams(window.location.search).get("at");
  const [activeTab, setActiveTab] = useState<AppTab>(() => tabFromHash());
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [aaRequest, setAaRequest] = useState(() => ({ version: new URLSearchParams(window.location.search).get("aaVersion") }));
  const [aaLoading, setAaLoading] = useState(false);
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null);
  const [snapshotRevision, setSnapshotRevision] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCompany, setActiveCompany] = useState<CompanySlug | "all">("all");
  const [activePeriod, setActivePeriod] = useState<LandscapePeriod>("all");
  const [benchmarkSelection] = useState<BenchmarkSelection>({ source: "artificial-analysis", metric: "intelligenceIndex" });
  const benchmarkSystem = benchmarkSelection.source;
  const metric: ScoreMetric = benchmarkSelection.source === "artificial-analysis" ? benchmarkSelection.metric : "intelligenceIndex";
  const epochMetric: EpochMetricId = benchmarkSelection.source === "epoch-eci" ? benchmarkSelection.metric : "general";
  const [priceMode, setPriceMode] = useState<PriceMode>("msrp");
  const [activityId, setActivityId] = useState<PricingActivityId>(DEFAULT_PRICING_ACTIVITY.id);
  const [coloringMode, setColoringMode] = useState<LandscapeColoringMode>("uniform");
  const tabletOrLarger = useMediaQuery("(min-width: 768px)");
  const [markerModeOverride, setMarkerMode] = useState<LandscapeMarkerMode | null>(null);
  const markerMode = markerModeOverride ?? (tabletOrLarger ? "dots" : "rings");
  const [modelSearch, setModelSearch] = useState("");
  const [showEstimates, setShowEstimates] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [landscapeWidth, setLandscapeWidth] = useState(960);
  const [landscapeFiltersOpen, setLandscapeFiltersOpen] = useState(false);
  const [countAnnouncement, setCountAnnouncement] = useState("");
  const [landscapeInteractionVersion, setLandscapeInteractionVersion] = useState(0);
  const [setLandscapeSummaryNode, landscapeSummaryOverflow] = useHorizontalOverflow<HTMLDivElement>();
  const landscapeShellRef = useRef<HTMLDivElement>(null);
  const landscapeFilterButtonRef = useRef<HTMLButtonElement>(null);
  const landscapeInteraction = useRef(false);

  useEffect(() => {
    const syncTab = (): void => {
      setActiveTab(tabFromHash());
      const version = new URLSearchParams(window.location.search).get("aaVersion");
      setAaRequest((current) => current.version === version ? current : { version });
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
    };
    syncTab();
    window.addEventListener("hashchange", syncTab);
    window.addEventListener("popstate", syncTab);
    return () => {
      window.removeEventListener("hashchange", syncTab);
      window.removeEventListener("popstate", syncTab);
    };
  }, []);

  const activateTab = (tab: AppTab): void => {
    const destination = tab === "home"
      ? `${window.location.pathname}${window.location.search}`
      : `${window.location.pathname}${window.location.search}#${tab}`;
    if (activeTab !== tab) window.history.pushState(null, "", destination);
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const handleTabKey = (event: React.KeyboardEvent<HTMLButtonElement>, tab: AppTab): void => {
    const index = APP_TABS.findIndex((candidate) => candidate.id === tab);
    const nextIndex = event.key === "Home" ? 0
      : event.key === "End" ? APP_TABS.length - 1
        : event.key === "ArrowRight" ? (index + 1) % APP_TABS.length
          : event.key === "ArrowLeft" ? (index - 1 + APP_TABS.length) % APP_TABS.length
            : null;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = APP_TABS[nextIndex]!;
    activateTab(next.id);
    requestAnimationFrame(() => document.getElementById(`tab-${next.id}`)?.focus());
  };

  useEffect(() => {
    let mounted = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();
    const load = async (): Promise<void> => {
      setAaLoading(true);
      let nextDelay = 300_000;
      try {
        const params = new URLSearchParams();
        if (requestedArchiveAt) params.set("at", requestedArchiveAt);
        if (aaRequest.version) params.set("aaVersion", aaRequest.version);
        const endpoint = `/api/dashboard${params.size ? `?${params}` : ""}`;
        const response = await fetch(endpoint, { cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal });
        if (!response.ok) throw new Error(`Dashboard returned ${response.status}`);
        const next = await response.json() as DashboardResponse;
        if (!next.models.length || next.jobs.some((job) => job.status === "running")) nextDelay = 3_000;
        if (mounted) {
          setData(next);
          setSnapshotAt(response.headers.get("X-Cadence-Snapshot-At"));
          setSnapshotRevision(response.headers.get("X-Cadence-App-Revision"));
          setError(null);
        }
      } catch (reason) {
        nextDelay = 10_000;
        if (mounted) {
          setError(reason instanceof Error ? reason.message : "Unable to load dashboard");
        }
      } finally {
        if (mounted) setAaLoading(false);
        if (mounted && !requestedArchiveAt) timer = setTimeout(() => void load(), nextDelay);
      }
    };
    void load();
    return () => { mounted = false; controller.abort(); if (timer) clearTimeout(timer); };
  }, [requestedArchiveAt, aaRequest]);

  useEffect(() => {
    if (!modelColorScheme(activeCompany) && coloringMode !== "uniform") setColoringMode("uniform");
  }, [activeCompany, coloringMode]);

  useEffect(() => {
    const shell = landscapeShellRef.current;
    if (!shell || typeof ResizeObserver !== "function") return undefined;
    const update = (): void => setLandscapeWidth(shell.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [data]);

  const costTierVersion = data?.methodology.costTierVersion ?? TASK_COST_TIER_VERSION;
  const priceModeOrder: readonly PriceMode[] = ["msrp", "best", "original"];
  const advertisedPriceModes: readonly PriceMode[] = data?.availablePriceModes ?? ["msrp", "best"];
  const availablePriceModes = priceModeOrder.filter((mode) => advertisedPriceModes.includes(mode));
  const activities = pricingActivitiesForVersion(costTierVersion);
  const effectiveActivityId = activities.some((candidate) => candidate.id === activityId) ? activityId : activities[0]?.id ?? DEFAULT_PRICING_ACTIVITY.id;
  const activity = pricingActivity(effectiveActivityId, costTierVersion);
  const effectivePriceMode = availablePriceModes.includes(priceMode) ? priceMode : availablePriceModes[0] ?? "msrp";
  useEffect(() => {
    if (priceMode !== effectivePriceMode) setPriceMode(effectivePriceMode);
  }, [priceMode, effectivePriceMode]);
  useEffect(() => {
    if (activityId !== effectiveActivityId) setActivityId(effectiveActivityId);
  }, [activityId, effectiveActivityId]);
  const filteredModels = useMemo(() => eligibleLandscapeModels(data?.models ?? [], {
    company: activeCompany,
    period: activePeriod,
    metric,
    selection: benchmarkSelection,
    priceMode: effectivePriceMode,
    costTierVersion,
    activity,
    showEstimates,
    query: modelSearch,
  }), [data, activeCompany, activePeriod, metric, benchmarkSystem, epochMetric, effectivePriceMode, costTierVersion, activity, showEstimates, modelSearch]);
  const landscapeCompact = landscapeWidth <= 840;
  // Match the wide SVG's usable canvas: two panel borders plus 45px/14px chart padding.
  const countGeometry = landscapeCompact
    ? { chartWidth: 768, scoreBaseline: 484, scoreSpan: 410 }
    : { chartWidth: Math.max(1, Math.round(landscapeWidth - 61)), scoreBaseline: 360, scoreSpan: 286 };
  const representedModelCount = useMemo(() => layoutCanonicalLandscapeModels({
    models: filteredModels,
    metric,
    selection: benchmarkSelection,
    priceMode: effectivePriceMode,
    activity,
    costTierVersion,
    tierScheme: tierSchemeForVersion(costTierVersion, activity),
    coloringMode: "uniform",
    colorScheme: null,
    companyColors: new Map(),
    ...countGeometry,
  }).points.length, [filteredModels, metric, benchmarkSystem, epochMetric, effectivePriceMode, activity, costTierVersion, countGeometry.chartWidth, countGeometry.scoreBaseline, countGeometry.scoreSpan]);
  const selected = selectedId ? filteredModels.find((model) => model.id === selectedId) ?? null : null;
  const landscapeCountText = `${representedModelCount} model${representedModelCount === 1 ? "" : "s"} shown.`;
  const eciMetricModels = benchmarkSelection.source === "epoch-eci"
    ? (data?.models ?? []).filter((model) => resolveModelScore(model, benchmarkSelection) !== null)
    : [];
  const eciMappedOverall = benchmarkSelection.source === "epoch-eci"
    ? data?.epochEci?.mappingCoverage?.mappedModelsByMetric[benchmarkSelection.metric] ?? eciMetricModels.length
    : 0;
  const eciMappedGroups = benchmarkSelection.source === "epoch-eci"
    ? data?.epochEci?.mappingCoverage?.mappedGroupsByMetric[benchmarkSelection.metric]
      ?? new Set(eciMetricModels.flatMap((model) => {
        const groupId = model.epochEciScores?.[benchmarkSelection.metric]?.groupId;
        return groupId ? [groupId] : [];
      })).size
    : 0;
  const eciSourceGroups = benchmarkSelection.source === "epoch-eci"
    ? data?.epochEci?.mappingCoverage?.sourceGroupsByMetric[benchmarkSelection.metric]
      ?? data?.epochEci?.groups.filter((group) => group.scores[benchmarkSelection.metric] !== null).length
      ?? 0
    : 0;
  const eciPricingOmitted = benchmarkSelection.source === "epoch-eci"
    ? eciMetricModels.filter((model) => effectivePricing(model, effectivePriceMode, activity, costTierVersion).value === null).length
    : 0;

  useEffect(() => {
    if (selectedId && !selected) setSelectedId(null);
  }, [selected, selectedId]);
  useEffect(() => {
    if (!landscapeInteraction.current) return undefined;
    landscapeInteraction.current = false;
    setCountAnnouncement("");
    const timer = window.setTimeout(() => setCountAnnouncement(landscapeCountText), 0);
    return () => window.clearTimeout(timer);
  }, [landscapeCountText, landscapeInteractionVersion]);

  const markLandscapeInteraction = (): void => { landscapeInteraction.current = true; setLandscapeInteractionVersion((version) => version + 1); };
  const closeLandscapeFilters = (restoreFocus = true): void => {
    setLandscapeFiltersOpen(false);
    if (restoreFocus) requestAnimationFrame(() => landscapeFilterButtonRef.current?.focus());
  };
  useEffect(() => {
    if (!landscapeFiltersOpen) return undefined;
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeLandscapeFilters();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [landscapeFiltersOpen]);
  const resetLandscape = (): void => {
    markLandscapeInteraction();
    setSelectedId(null);
    setMarkerMode(null);
    setModelSearch("");
    setActiveCompany("all");
    setActivePeriod("all");
    setPriceMode(availablePriceModes.includes("msrp") ? "msrp" : availablePriceModes[0] ?? "msrp");
    setActivityId(activities.some((candidate) => candidate.id === DEFAULT_PRICING_ACTIVITY.id) ? DEFAULT_PRICING_ACTIVITY.id : activities[0]?.id ?? DEFAULT_PRICING_ACTIVITY.id);
    setColoringMode("uniform");
    setShowEstimates(true);
  };

  if (error && !data) return <main className="fatal" aria-live="assertive"><p className="eyebrow">Connection lost</p><h1>Cadence is offline.</h1><p>{error}</p><button type="button" onClick={() => location.reload()}>Try again</button></main>;
  if (!data) return <main className="loading" aria-busy="true" aria-live="polite"><span className="loader" aria-hidden="true" /><p>Reading the landscape…</p></main>;
  if (!data.models.length && !data.epochEci?.groups.length) return <EmptyState />;

  const companies = data.companies;
  const companiesBySlug = companyMap(companies);
  const colorScheme = modelColorScheme(activeCompany);
  const taskPricing = isTaskPricingVersion(costTierVersion);
  const tierScheme = tierSchemeForVersion(costTierVersion, activity);
  const aaVersionLabel = data.aaVersions.find((version) => version.id === data.aaVersion)?.label ?? `AA v${data.aaVersion}`;
  const aaVersionPicker = (
    <div className="aa-version-toolbar">
      <label className="aa-version-picker"><span>AA version</span>
        <select className="aa-version-select" value={data.aaVersion} disabled={aaLoading} onChange={(event) => {
          const version = event.target.value;
          const url = new URL(window.location.href);
          url.searchParams.set("aaVersion", version);
          if (url.href !== window.location.href) window.history.pushState(null, "", url);
          setError(null);
          setAaLoading(true);
          setAaRequest({ version });
        }}>
          {data.aaVersions.map((version) => <option key={version.id} value={version.id}>{version.label}</option>)}
        </select>
      </label>
      {aaLoading && <span role="status">Loading scores… Displayed scores remain {aaVersionLabel}.</span>}
    </div>
  );
  const activeCompanyLabel = activeCompany === "all" ? "All labs" : companiesBySlug.get(activeCompany)?.name ?? activeCompany;
  const activePeriodLabel = activePeriod === "all"
    ? "All releases"
    : LANDSCAPE_PERIOD_OPTIONS.find((option) => option.value === activePeriod)?.label ?? activePeriod;
  const activePriceLabel = effectivePriceMode === "msrp" ? "MSRP" : effectivePriceMode === "best" ? "Best" : "Original";
  const landscapeFilterSummary = [
    { key: "company", label: activeCompanyLabel },
    { key: "release", label: activePeriodLabel },
    { key: "price", label: activePriceLabel },
    { key: "markers", label: markerMode === "dots" ? "Dots" : "Rings" },
    ...(taskPricing ? [{ key: "activity", label: activity.label }] : []),
    ...(colorScheme && coloringMode !== "uniform" ? [{ key: "coloring", label: "By name color" }] : []),
    ...(benchmarkSystem === "artificial-analysis" && !showEstimates ? [{ key: "estimates", label: "AA estimates hidden" }] : []),
  ];
  const landscapeActiveFilterCount = 2
    + (taskPricing ? 1 : 0)
    + (activeCompany === "all" ? 0 : 1)
    + (activePeriod === "all" ? 0 : 1)
    + (colorScheme && coloringMode !== "uniform" ? 1 : 0)
    + (benchmarkSystem === "artificial-analysis" && !showEstimates ? 1 : 0);
  const homePriceModeLabel = effectivePriceMode === "msrp" ? "MSRP" : effectivePriceMode === "best" ? "Best" : "Original";
  const homePricingContext = taskPricing ? activity.label : "Blended price / M";
  const latestArrivals = selectLatestScoredPricedModels(data.models, effectivePriceMode, activity, costTierVersion, 5, benchmarkSelection);
  const latestScored = latestArrivals[0] ?? null;
  const selectedScore = (model: ModelView): number => resolveModelScore(model, benchmarkSelection)?.value ?? Number.NaN;
  const selectedBenchmarkLabel = benchmarkSelection.source === "epoch-eci"
    ? `Epoch ECI ${EPOCH_METRICS.find((item) => item.key === benchmarkSelection.metric)?.label}`
    : `${aaVersionLabel} ${METRICS.find((item) => item.key === benchmarkSelection.metric)?.short}`;
  const scoredPeers = latestScored ? data.models.filter((model) => model.id !== latestScored.id
    && resolveModelScore(model, benchmarkSelection)?.estimated === false
    && effectivePricing(model, effectivePriceMode, activity, costTierVersion).value !== null) : [];
  const latestPricing = latestScored ? effectivePricing(latestScored, effectivePriceMode, activity, costTierVersion) : null;
  const latestBand = latestPricing ? priceBandFor(latestPricing, tierScheme) : "unknown";
  const sameTier = latestPricing && latestBand !== "unknown" ? scoredPeers.filter((model) => priceBandFor(effectivePricing(model, effectivePriceMode, activity, costTierVersion), tierScheme) === latestBand) : [];
  const tierPeer = latestScored ? [...sameTier].sort((a, b) => Math.abs(selectedScore(a) - selectedScore(latestScored)) - Math.abs(selectedScore(b) - selectedScore(latestScored)) || a.id.localeCompare(b.id))[0] : null;
  const nearestHigher = latestScored ? [...scoredPeers].filter((model) => selectedScore(model) > selectedScore(latestScored)).sort((a, b) => selectedScore(a) - selectedScore(b) || a.id.localeCompare(b.id))[0] : null;
  const nearestLower = latestScored ? [...scoredPeers].filter((model) => selectedScore(model) <= selectedScore(latestScored)).sort((a, b) => selectedScore(b) - selectedScore(a) || a.id.localeCompare(b.id))[0] : null;
  const activeRumors = data.rumors;
  const boardModels = benchmarkSelection.source === "epoch-eci" || showEstimates
    ? data.models
    : data.models.filter((model) => resolveModelScore(model, benchmarkSelection)?.estimated === false);
  const primaryTab = activeTab === "signals" ? "cadence" : activeTab;

  return (
    <>
      <a className="skip-link" href="#top">Skip to main content</a>
      <header className="site-header">
        <a id="cadence-home" className="brand" href={`${window.location.pathname}${window.location.search}`} aria-label="Cadence home" aria-controls="panel-home" aria-current={activeTab === "home" ? "page" : undefined} onClick={(event) => { event.preventDefault(); activateTab("home"); }}><span className="brand-glyph" aria-hidden="true"><i /><i /><i /></span></a>
        <nav aria-label="Cadence views">
          {APP_TABS.map((tab) => <button type="button" key={tab.id} id={`tab-${tab.id}`} aria-current={primaryTab === tab.id ? "page" : undefined} aria-controls={`panel-${tab.id}`} className={primaryTab === tab.id ? "active" : ""} onClick={() => activateTab(tab.id)} onKeyDown={(event) => handleTabKey(event, tab.id)}>{tab.label}</button>)}
        </nav>
      </header>

      <main id="top" tabIndex={-1}>
        {error && data && <p className="page-width aa-version-status" role="alert">Unable to update the dashboard: {error}. Displayed scores remain {aaVersionLabel}.</p>}
        {snapshotAt && requestedArchiveAt && (
          <section className="archive-banner" aria-label="Archived dashboard details">
            <strong>Immutable archive</strong>
            <span>Requested {requestedArchiveAt}</span>
            <span>Restored capture {snapshotAt.replace("T", " ").replace("Z", " UTC")}</span>
            {snapshotRevision && <code>{snapshotRevision.slice(0, 12)}</code>}
            <a href="/">Return to live dashboard ↗</a>
          </section>
        )}
        <div id="panel-home" className="tab-panel home-panel" role="region" aria-labelledby="tab-home" hidden={activeTab !== "home"}>
        <section className="start-content page-width" aria-label="Latest models">
          <section className="latest-callout" aria-labelledby="latest-arrivals-heading" style={{ "--company": latestScored ? companiesBySlug.get(latestScored.companySlug)?.color : "var(--acid)" } as React.CSSProperties}>
            <div className="callout-orbit"><span /><span /></div>
            <div className="latest-heading">
              <h2 className="eyebrow" id="latest-arrivals-heading">Latest scored arrivals</h2>
              <span>{homePricingContext} · {homePriceModeLabel}</span>
            </div>
            {latestArrivals.length === 0 ? <p className="latest-empty">No measured and priced releases for {selectedBenchmarkLabel} in this view.</p> : (
              <ol className="latest-list">
                {latestArrivals.map((model, index) => {
                  const company = companiesBySlug.get(model.companySlug);
                  const pricing = effectivePricing(model, effectivePriceMode, activity, costTierVersion);
                  const band = priceBandFor(pricing, tierScheme);
                  const releaseDate = modelReleaseDate(model, effectivePriceMode);
                  const priceContext = pricing.isTaskPrice ? `per ${activity.label.toLowerCase()} task` : "per M blended";
                  const sourceContext = `${pricing.source}${pricing.fallback ? " · fallback" : ""}${pricing.cacheFallback ? " · cache→input" : ""}`;
                  if (index === 0) return (
                    <li className="latest-featured" key={model.id} style={{ "--arrival-company": company?.color ?? "#fff" } as React.CSSProperties}>
                      <div className="latest-featured-name"><span><i /><b>{company?.shortName ?? model.companyName}</b><em><span className="pulse" />Newest scored</em></span><h3>{model.shortName}</h3></div>
                      <div className="latest-score"><strong>{score(selectedScore(model))}</strong><span>{selectedBenchmarkLabel}{benchmarkSelection.source === "artificial-analysis" && selectedBenchmarkEffort(model) && <><br />{selectedBenchmarkEffort(model)} effort</>}</span></div>
                      <p className="latest-comparison">
                        <b>{tierScheme.tiers[band].name}</b> at {money(pricing.value)} {priceContext}.
                        {tierPeer && <> Priced alongside <b>{tierPeer.shortName}</b>.</>}
                        {nearestLower && <> Ahead of <b>{nearestLower.shortName}</b>.</>}
                        {nearestHigher && <> Just behind <b>{nearestHigher.shortName}</b>.</>}
                      </p>
                      <p className="latest-meta"><span>{releaseDate?.provenance} {formatDate(releaseDate?.displayValue ?? null)}</span><span>{sourceContext}</span></p>
                    </li>
                  );
                  return (
                    <li className="latest-compact" key={model.id} style={{ "--arrival-company": company?.color ?? "#fff" } as React.CSSProperties}>
                      <div className="latest-compact-identity"><span><i />{company?.shortName ?? model.companyName}</span><strong>{model.shortName}</strong><small>{releaseDate?.provenance} {formatDate(releaseDate?.displayValue ?? null)}</small></div>
                      <strong className="latest-compact-score">{score(selectedScore(model))}</strong>
                      <p><span><b>{tierScheme.tiers[band].name}</b> · {money(pricing.value)} {priceContext}</span><small>{sourceContext}</small></p>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
          {!requestedArchiveAt && <div className="collector-status"><StatusPill jobs={data.jobs} />{data.jobs.filter((job) => job.status === "failed").map((job) => <p key={job.jobType}>{job.error?.includes("Reddit listing returned HTTP 403") ? "Reddit returned HTTP 403. Reddit release detection is incomplete." : `${job.jobType}: ${job.error?.split("\n")[0] ?? "Collection failed; retry pending."}`}</p>)}</div>}
          {!requestedArchiveAt && <ReleaseCandidates />}
        </section>
        </div>

        <div id="panel-landscape" className="tab-panel" role="region" aria-labelledby="tab-landscape" hidden={activeTab !== "landscape"}>
        <section className="section page-width" id="landscape">
          <div className="section-heading landscape-heading">
            <div><h2>Capability, priced.</h2><p>Higher scores ↑ · Higher task cost →</p></div>
            <div className="landscape-count-card">
              <output aria-label={landscapeCountText}>{representedModelCount}</output><span aria-hidden="true">models</span>
            </div>
          </div>
          {aaVersionPicker}
          <div className="landscape-workspace-shell" ref={(node) => { landscapeShellRef.current = node; if (node) { const width = node.getBoundingClientRect().width; if (width > 0 && Math.abs(width - landscapeWidth) > 1) requestAnimationFrame(() => setLandscapeWidth(width)); } }}>
            <div className="landscape-workspace">
              <div className="landscape-focusbar">
                {taskPricing ? <div className="landscape-task"><ActivityFilters activities={activities} active={effectiveActivityId} onChange={(value) => { markLandscapeInteraction(); setActivityId(value); }} /></div> : <span className="source-note legacy-pricing">Blended price / M</span>}
                <button ref={landscapeFilterButtonRef} type="button" className="landscape-filter-button" aria-expanded={landscapeFiltersOpen} aria-controls={landscapeFiltersOpen ? "landscape-filter-drawer" : undefined} onClick={() => setLandscapeFiltersOpen((open) => !open)}>
                  Filters <b>{landscapeActiveFilterCount}</b>
                </button>
                <div className="landscape-search">
                  <label className="sr-only" htmlFor="landscape-model-search">Search models</label>
                  <input id="landscape-model-search" type="search" placeholder="Search models…" value={modelSearch} onChange={(event) => { markLandscapeInteraction(); setModelSearch(event.target.value); }} />
                  {modelSearch && <button type="button" aria-label="Clear model search" onClick={() => { markLandscapeInteraction(); setModelSearch(""); document.getElementById("landscape-model-search")?.focus(); }}>Clear</button>}
                  <output aria-label={landscapeCountText}>{representedModelCount} {representedModelCount === 1 ? "model" : "models"}</output>
                </div>
              </div>

              {landscapeFiltersOpen && <aside id="landscape-filter-drawer" className="landscape-filter-drawer" aria-label="All landscape filters">
                <div className="landscape-drawer-head">
                  <div><span className="section-index">Refine landscape</span><h3 id="landscape-filter-drawer-heading">Filters</h3></div>
                  <button type="button" className="landscape-drawer-close" onClick={() => closeLandscapeFilters()} aria-label="Close filters">×</button>
                </div>
              <div ref={setLandscapeSummaryNode} className="landscape-filter-summary" tabIndex={landscapeSummaryOverflow ? 0 : -1} aria-label={landscapeSummaryOverflow ? "Scrollable active filters. Use Left and Right arrow keys to review every filter." : "Active filters"} onKeyDown={navigateHorizontalOverflow}>
                <span>Active</span>
                {landscapeFilterSummary.map((item) => <i key={item.key}>{item.label}</i>)}
                {benchmarkSelection.source === "epoch-eci" && <i className="landscape-coverage-chip">{eciMappedGroups} / {eciSourceGroups} source groups · {eciMappedOverall} mapped · {representedModelCount} plotted · {eciPricingOmitted} omitted by {activePriceLabel}</i>}
                {markerMode === "rings" && <i>Count is models, not rings</i>}
              </div>

                <fieldset className="landscape-drawer-row"><legend className="sr-only">Lab</legend><span className="landscape-drawer-row-label" id="landscape-lab-label">Lab</span><LandscapeCompanySelect labelledBy="landscape-lab-label" companies={companies} active={activeCompany} onChange={(value) => { markLandscapeInteraction(); setActiveCompany(value); }} /></fieldset>
                {taskPricing && <p className="rail-note">{activity.sessionLabel} · {activity.description}</p>}
                <fieldset className="landscape-drawer-row"><legend className="sr-only">Markers</legend><span className="landscape-drawer-row-label">Markers</span><MarkerModeFilters active={markerMode} onChange={(value) => { markLandscapeInteraction(); setMarkerMode(value); }} /></fieldset>
                <fieldset className="landscape-drawer-row"><legend className="sr-only">Release</legend><span className="landscape-drawer-row-label">Release</span><PeriodFilters active={activePeriod} onChange={(value) => { markLandscapeInteraction(); setActivePeriod(value); }} />{activePeriod === "best" && <p className="rail-note best-view-explainer">{benchmarkSystem === "epoch-eci" ? "Best keeps each lab’s strongest non-dominated model per band using only the selected ECI central estimate." : "Best removes models outclassed across all three AA benchmarks by a cheaper tier, then keeps each lab’s top remaining model per band."}</p>}</fieldset>
                <fieldset className="landscape-drawer-row"><legend className="sr-only">Price</legend><span className="landscape-drawer-row-label">Price</span><div className="price-mode-toggle rail-choice-group" role="radiogroup" aria-label="Pricing source">{availablePriceModes.map((mode) => <button key={mode} role="radio" tabIndex={effectivePriceMode === mode ? 0 : -1} aria-checked={effectivePriceMode === mode} className={effectivePriceMode === mode ? "active" : ""} aria-label={mode === "msrp" ? "Current creator MSRP" : mode === "best" ? "Lowest current complete route" : "Original launch MSRP"} onKeyDown={(event) => radioKey(event, availablePriceModes, effectivePriceMode, (value) => { markLandscapeInteraction(); setPriceMode(value); })} onClick={() => { markLandscapeInteraction(); setPriceMode(mode); }}>{mode === "msrp" ? "MSRP" : mode === "best" ? "Best" : "Original"}</button>)}</div>{effectivePriceMode === "best" && <p className="rail-note">Lowest current complete route.</p>}</fieldset>
                {!taskPricing && <div className="landscape-drawer-row landscape-legacy-row"><span className="landscape-drawer-row-label">Activity</span><p className="rail-note legacy-pricing"><span>Archived method</span> This snapshot keeps its original blended price per million tokens and price bands.</p></div>}
                {colorScheme && <fieldset className="landscape-drawer-row"><legend className="sr-only">Coloring</legend><span className="landscape-drawer-row-label">Coloring</span><ColoringFilters active={coloringMode} onChange={(value) => { markLandscapeInteraction(); setColoringMode(value); }} /></fieldset>}
                <fieldset className="landscape-drawer-row"><legend className="sr-only">Estimates</legend><span className="landscape-drawer-row-label">Estimates</span><label title={benchmarkSystem === "epoch-eci" ? "No Cadence estimates are used for ECI." : undefined} className={`estimate-toggle landscape-estimate-toggle ${showEstimates ? "active" : ""}`}><input type="checkbox" disabled={benchmarkSystem === "epoch-eci"} checked={showEstimates} onChange={(event) => { markLandscapeInteraction(); setShowEstimates(event.target.checked); }} /><i aria-hidden="true" /><span>{benchmarkSystem === "epoch-eci" ? "No Cadence estimates are used for ECI." : "Show AA estimates"}</span><b>{benchmarkSystem === "epoch-eci" ? "N/A" : showEstimates ? "Shown" : "Hidden"}</b></label></fieldset>

                <div className="landscape-drawer-foot">
                  <button type="button" className="landscape-reset" onClick={resetLandscape}>Reset landscape</button>
                  <span>Current selection</span>
                  <strong>{representedModelCount} models</strong>
                </div>
              </aside>}

              <section className="landscape-canvas-column" aria-label="Landscape chart workspace">
                <div className="landscape-panel">
                  {taskPricing && <p className="task-context"><span>{activity.sessionLabel} · {activePriceLabel}</span><span>{Math.round(activity.cacheShare * 100)}% cache · {Math.round(activity.inputShare * 100)}% input · {Math.round(activity.outputShare * 100)}% output</span></p>}
                  <LandscapeChart key={`${benchmarkSystem}-${data.aaVersion}-${epochMetric}-${markerMode}`} models={filteredModels} companies={companies} metric={metric} selection={benchmarkSelection} aaVersionLabel={aaVersionLabel} priceMode={effectivePriceMode} activity={activity} costTierVersion={costTierVersion} tierScheme={tierScheme} coloringMode={colorScheme ? coloringMode : "uniform"} colorScheme={colorScheme} markerMode={markerMode} selectedId={selected?.id ?? null} isVisible={activeTab === "landscape"} compact={landscapeCompact} archiveWithoutProjections={snapshotAt !== null && !data.models.some((model) => model.epochEciScores && Object.keys(model.epochEciScores).length > 0)} onSelect={setSelectedId} />
                  {selected && <ModelInspector model={selected} company={companiesBySlug.get(selected.companySlug)} aaVersionLabel={aaVersionLabel} priceMode={effectivePriceMode} activity={activity} costTierVersion={costTierVersion} />}
                </div>
              </section>
            </div>
          </div>
          <div className="landscape-links"><button type="button" onClick={() => activateTab("models")}>Browse model ranks ↗</button><button type="button" onClick={() => activateTab("methodology")}>Sources & methodology ↗</button></div>
          <div className="sr-only landscape-count-announcement" aria-live="polite" aria-atomic="true">{countAnnouncement}</div>
        </section>
        </div>

        <div id="panel-changes" className="tab-panel" role="region" aria-labelledby="tab-changes" hidden={activeTab !== "changes"}>
          <section className="section page-width changes-section" id="changes">
            <div className="section-heading changes-heading">
              <div><p className="section-index">RELEASE BY RELEASE</p><h2>When capability moves.</h2><p>New measured bests for each lab. No calendar buckets, no pricing assumptions.</p></div>
            </div>
            {aaVersionPicker}
            <ChangesPanel models={data.models} companies={companies} selection={benchmarkSelection} aaVersionLabel={aaVersionLabel} referenceDate={data.generatedAt} />
          </section>
        </div>

        <div id="panel-cadence" className="tab-panel" role="region" aria-labelledby="tab-cadence" hidden={activeTab !== "cadence"}>
        <section className="section cadence-section" id="cadence">
          <div className="page-width">
            <div className="cadence-heading">
              <div className="cadence-legend"><span><i className="solid" />release</span><span><i className="hollow" />projected beat</span></div>
            </div>
            <div className="cadence-labels"><span>Company</span><span>Typical interval</span><span>18 months past / 6 months future</span><span>First unfilled beat</span></div>
            <CadenceRows companies={companies} active={activeCompany} referenceDate={data.generatedAt} onSelect={(slug) => setActiveCompany(activeCompany === slug ? "all" : slug)} />
            <div className="landscape-links"><button type="button" onClick={() => activateTab("signals")}>Evidence-backed signals ↗</button></div>
          </div>
        </section>
        </div>

        <div id="panel-signals" className="tab-panel" role="region" aria-label="Release signals" hidden={activeTab !== "signals"}>
        <section className="section page-width" id="signals">
          <button type="button" className="back-link" onClick={() => activateTab("cadence")}>← Cadence</button>
          <div className="section-heading signals-heading">
            <div><p className="section-index">04 / SIGNAL WATCH</p><h2>What might move next.</h2><p>A daily deep scan, with evidence and uncertainty kept attached.</p></div>
            <span className="scan-stamp">Deep scan · {relativeTime(data.jobs.find((job) => job.jobType === "discovery")?.finishedAt ?? null, snapshotAt ? new Date(data.generatedAt).getTime() : Date.now())}</span>
          </div>
          {activeRumors.length ? (
            <div className="signals-grid">{activeRumors.slice(0, 6).map((rumor) => <RumorCard key={rumor.id} rumor={rumor} company={companiesBySlug.get(rumor.companySlug)} />)}</div>
          ) : (
            <div className="signals-empty"><span className="radar"><i /><i /><i /></span><div><h3>No evidence-backed signals in this view.</h3><p>That is a result, not a gap. The daily scan will only publish claims with a source.</p></div></div>
          )}
        </section>
        </div>

        <div id="panel-models" className="tab-panel" role="region" aria-label="Model ranks" hidden={activeTab !== "models"}>
        <section className="models-section" id="models">
          <div className="page-width">
            {aaVersionPicker}
            <ComparisonTable models={boardModels} companies={companies} metric={metric} selection={benchmarkSelection} aaVersionLabel={aaVersionLabel} priceMode={effectivePriceMode} activity={activity} costTierVersion={costTierVersion} tierScheme={tierScheme} />
          </div>
        </section>
        </div>

        <div id="panel-methodology" className="tab-panel" role="region" aria-label="Sources and methodology" hidden={activeTab !== "methodology"}>
        <section className="methodology page-width" id="methodology">
          <div><p className="section-index">METHOD, NOT MAGIC</p><h2>The useful lie is labeled.</h2></div>
          <div className="method-grid">
            <article><span>01</span><h3>Intelligence</h3><p>{data.methodology.intelligenceAttribution} It is the headline orientation metric, not a universal verdict.</p></article>
            <article><span>02</span><h3>Price band</h3><p>{data.methodology.costFormula} {data.methodology.pricingCaveat} Fixed bands make unlike pricing easier to scan.</p></article>
            <article><span>03</span><h3>Cadence</h3><p>{data.methodology.cadenceFormula} {data.methodology.releaseDateCaveat}</p></article>
            <article className="caution"><span>04</span><h3>Output / task</h3><p>{data.methodology.tokenEfficiencyCaveat}</p></article>
            <article><span>05</span><h3>Epoch model ECI</h3><p>Epoch ECI is an unbounded arbitrary linear latent-capability scale. Cadence projects a source observation through reviewed retained member identities; one group may qualify several Cadence models while each keeps its own price coordinate. Cyber remains a separate published fit and is mapped only through its explicit General-group joins. Genuinely ambiguous groups stay source-only. Software engineering and Math remain unavailable until Cadence's clean-room refit matches reviewed explorer fixtures.</p></article>
          </div>
        </section>
        </div>
      </main>

      <footer>
        <div className="page-width footer-inner">
          <div className="footer-brand"><span className="brand-glyph"><i /><i /><i /></span><strong>CADENCE</strong><p>A fast map of a landscape that refuses to sit still.</p></div>
          <div className="footer-links"><a href="https://openrouter.ai/models" target="_blank" rel="noreferrer">OpenRouter ↗</a><a href="https://artificialanalysis.ai/" target="_blank" rel="noreferrer">Artificial Analysis ↗</a><a href="https://epoch.ai/eci" target="_blank" rel="noreferrer">Epoch ECI ↗</a><a href="#methodology" onClick={(event) => { event.preventDefault(); activateTab("methodology"); }}>Methodology</a></div>
          <label className="archive-picker"><span>View archived date</span><input type="date" value={snapshotAt?.slice(0, 10) ?? ""} max={new Date().toISOString().slice(0, 10)} onChange={(event) => {
            if (!event.target.value) return;
            const url = new URL(window.location.href);
            url.searchParams.set("at", event.target.value);
            url.searchParams.delete("aaVersion");
            url.hash = "";
            window.location.assign(url);
          }} /></label>
          <p className="footer-freshness">{snapshotAt ? `Snapshot captured ${formatDate(snapshotAt)}` : `Page assembled ${relativeTime(data.generatedAt)}`}<br />Automated data can be wrong. Follow the sources.</p>
        </div>
      </footer>
    </>
  );
}

createRoot(document.getElementById("root")!).render(<React.StrictMode><App /></React.StrictMode>);
